import { uiText } from '../i18n/uiText';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Dialog from '../components/common/Dialog';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import useDraftBuffer from '../hooks/useDraftBuffer';
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import {EMPTY_MEETING_FORM,meetingToForm} from '../utils/meetingForm';
import { createSaveQueue, snapshotValues } from '../utils/draftBuffer';
import { selectChecklistAnswers } from '../utils/checklistAnswers';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  auditsApi, commentsApi, filesApi, issuesApi,
  auditAssignmentsApi, workspaceApi, procedureControlApi, procedureWorkflowApi,
  auditMeetingsApi, auditProgramsApi, auditReportsApi, correctivePlansApi,
  getApiErrorMessage, getApiErrorDetail,
  correctiveActionsApi,
  documentSignaturesApi,
} from '../api';
import { useAuth } from '../store/AuthContext';
import {
  ArrowLeft, Download, Send, CheckCircle, Play, Clock, MessageCircle,
  PlusCircle, AlertTriangle, ExternalLink, CalendarDays, Users,
  ClipboardList, FileText, BookOpen, Wrench, ChevronDown, ChevronUp,
  Eye, Building2, MapPin, UserCheck, X, ArrowRight, Lock, Save, Upload,
  LayoutGrid,
} from 'lucide-react';
import ChecklistSection from '../components/audits/ChecklistSection';
import ChecklistSubmission from '../components/audits/ChecklistSubmission';
import AuditClassifier from '../components/classifier/AuditClassifier';
import TechnicalRecords from '../components/classifier/TechnicalRecords';
import ChecklistWorkbench from '../components/audits/ChecklistWorkbench';
import { rememberAudit } from '../utils/recentWorkspace';
import AuditBlockerList from '../components/audits/AuditBlockerList';
import AuditReadiness from '../components/audits/AuditReadiness';
import SaveStatus from '../components/common/SaveStatus';
import {questionHasAnswer} from '../utils/checklistNavigation';
import ExpectedAnswerModal from '../components/audits/ExpectedAnswerModal';
import AuditReportExportModal from '../components/audits/AuditReportExportModal';
import RemoveAuditQuestionDialog from '../components/audits/RemoveAuditQuestionDialog';
import RemovedAuditQuestions from '../components/audits/RemovedAuditQuestions';
import QuestionDiscussion from '../components/audits/QuestionDiscussion';
import AssignAuditorsModal from '../components/audits/AssignAuditorsModal';
import ApplySanctionsButton from '../components/audits/ApplySanctionsButton';
import SelfCheckSummary from '../components/audits/SelfCheckSummary';
import AttachmentItem from '../components/common/AttachmentItem';
import EvidenceBrowser from '../components/common/EvidenceBrowser';
import Badge from '../components/ui/Badge';
import StatusBadge from '../components/ui/StatusBadge';
import PageIntro from '../components/ui/PageIntro';
import ProcessStepper from '../components/common/ProcessStepper';
import { Disclosure } from '../components/common/WorkspaceSummary';
import AnswerConflictDialog from '../components/audits/AnswerConflictDialog';
import SafeDateInput, { isSafeIsoDate } from '../components/common/SafeDateInput';
import { canAnswerAuditQuestion, isAuditeeRole } from '../utils/permissions';
import { safeInternalRoute } from '../utils/safeNavigation';
import { useConfirm, usePrompt } from '../store/ConfirmContext';
import { useToast } from '../store/ToastContext';

const fmt = (dt) => {
  if (!dt) return '—';
  try {
    const d = new Date(dt);
    const mo = [uiText("янв"),uiText("фев"),uiText("мар"),uiText("апр"),uiText("май"),uiText("июн"),uiText("июл"),uiText("авг"),uiText("сен"),uiText("окт"),uiText("ноя"),uiText("дек")];
    return `${d.getDate()} ${mo[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return dt; }
};
const fmtTime = (dt) => {
  if (!dt) return '';
  try { return new Date(dt).toLocaleTimeString('ru-RU',{hour:'2-digit',minute:'2-digit'}); } catch { return ''; }
};

const questionFrontendKey = (question) => (
  question.frontend_key
  || (question.extra_question_id ? `extra-${question.extra_question_id}` : `q-${question.question_id || question.id}`)
);

const buildScopedAnswerPayload = ({ answers, questions, user, enabled }) => {
  const questionsByKey = new Map((questions || []).map((question) => [questionFrontendKey(question), question]));

  return Object.entries(answers || {})
    .filter(([key]) => {
      const question = questionsByKey.get(key);
      if (!question) return false;
      const scope = question.effective_answer_scope || question.answer_scope || 'auditor';
      return canAnswerAuditQuestion(user, scope, enabled);
    })
    .map(([, answer]) => ({
      question_id: answer.question_id || null,
      extra_question_id: answer.extra_question_id || null,
      value_text: answer.value_text || null,
      value_number: answer.value_number !== '' && answer.value_number != null ? Number(answer.value_number) : null,
      value_bool: answer.value_bool != null ? answer.value_bool : null,
      comment_text: answer.comment_text || null,
      expected_answer_revision: Object.prototype.hasOwnProperty.call(answer, 'expected_answer_revision') ? answer.expected_answer_revision : answer.answer_revision ?? null,
    }))
    .filter((answer) => answer.question_id != null || answer.extra_question_id != null);
};

const STATUS_MAP = {
  draft:        { label:uiText("Черновик"),              cls:'badge-gray',   icon:<Clock size={12}/> },
  not_started: { label:uiText("Не начат"),           cls:'badge-gray',   icon:<Clock size={12}/> },
  program_sent: { label:uiText("План отправлен"), cls:'badge-blue',   icon:<Send size={12}/> },
  program_confirmed: { label:uiText("План согласован"), cls:'badge-blue', icon:<CheckCircle size={12}/> },
  in_progress:  { label:uiText("В работе"),           cls:'badge-blue',   icon:<Play size={12}/> },
  ready:        { label:uiText("Готов к закрытию"),   cls:'badge-orange', icon:<CheckCircle size={12}/> },
  awaiting_closure: { label:uiText("Готов к закрытию"), cls:'badge-orange', icon:<CheckCircle size={12}/> },
  completed:    { label:uiText("Завершён"),           cls:'badge-green',  icon:<CheckCircle size={12}/> },
  closed:       { label:uiText("Закрыт"),              cls:'badge-green',  icon:<CheckCircle size={12}/> },
  suspended:    { label:uiText("Приостановлен"),       cls:'badge-orange', icon:<Clock size={12}/> },
};

const ISSUE_STATUS = [
  {value:'open',        label:uiText("Открыто")},
  {value:'in_progress', label:uiText("В работе")},
  {value:'resolved',    label:uiText("Решено")},
  {value:'closed',      label:uiText("Закрыто")},
];

const ISSUE_PRIORITY = [
  {value:'critical',      label:uiText("Критическое несоответствие")},
  {value:'significant',   label:uiText("Значительное несоответствие")},
  {value:'minor',         label:uiText("Незначительное несоответствие")},
  {value:'recommendation',label:uiText("Рекомендация аудитора")},
];

const ISSUE_PRIORITY_LABELS = {
  major: uiText("Значительное (Major)"), ofi: uiText("Возможность для улучшения (OFI)"), sp: uiText("Сильная сторона (SP)"),
  ...Object.fromEntries(ISSUE_PRIORITY.map((item) => [item.value, item.label])),
  high: uiText("Значительное несоответствие"),
  medium: uiText("Незначительное несоответствие"),
  low: uiText("Рекомендация аудитора"),
};

const ISSUE_STATUS_LABELS = {
  ...Object.fromEntries(ISSUE_STATUS.map((item) => [item.value, item.label])),
  escalated: uiText("Просрочено, требуется внимание"),
  overdue: uiText("Просрочено, требуется внимание"),
};

const MEETING_TYPE_LABELS = {
  opening:      uiText("Вводное совещание"),
  intermediate: uiText("Промежуточное совещание"),
  closing:      uiText("Итоговое совещание"),
};

const EXTRA_Q_TYPES = [
  {value:'text',     label:uiText("Текст")},
  {value:'yesno',    label:uiText("Да / Нет")},
  {value:'number',   label:uiText("Число")},
  {value:'select',   label:uiText("Список")},
  {value:'photo',    label:uiText("Файл / фото")},
  {value:'signature',label:uiText("Подпись")},
];

const SCOPE_TEXT = {auditor:uiText("Аудитор"),customer:uiText("Проверяемый"),both:uiText("Оба участника")};

const AUDIT_TYPE_LABELS = {
  internal_operational: uiText("Внутренний операционный аудит"),
  special: uiText("Специальная проверка"),
  compliance: uiText("Аудит соответствия"),
  capability: uiText("Аудит производственных мощностей"),
  management_system: uiText("Аудит системы менеджмента"),
  extended_initial: uiText("Первичный аудит по расширенному регламенту"),
  extended_regular: uiText("Плановый аудит по расширенному регламенту"),
  extended_unplanned: uiText("Внеплановый аудит по расширенному регламенту"),
  extended_thematic: uiText("Тематический аудит по расширенному регламенту"),
  supplier_conformity: uiText("Аудит соответствия поставщика"),
  equipment_rbi: uiText("Аудит оборудования (RBI)"),
  franchise_regular: uiText("Плановый франчайзинговый аудит"),
  pressure_vessel: uiText("Аудит оборудования под давлением"),
};

const SUBJECT_KIND_LABELS = {
  internal_subdivision: uiText("Подразделение заказчика"),
  supplier: uiText("Поставщик"),
  contractor: uiText("Подрядчик"),
  partner: uiText("Партнёр сети"),
};

const CONTROL_MODE_META = {
  none: { label: uiText("Контроль не нужен"), variant: 'neutral', hint: uiText("Аудит можно закрыть без отдельной верификации.") },
  aas: { label: uiText("Контроль ведёт ААС"), variant: 'info', hint: uiText("Верификацию и контроль выполнения ведёт команда ААС.") },
  customer: { label: uiText("Контроль ведёт заказчик"), variant: 'warning', hint: uiText("Исполнение контролирует владелец процесса или заказчик.") },
};

const CORRECTIVE_STATUS = [
  { value: 'open', label: uiText("Ожидает ПКПД") },
  { value: 'in_progress', label: uiText("ПКПД в работе") },
  { value: 'resolved', label: uiText("Отправлено на верификацию") },
  { value: 'closed', label: uiText("Верифицировано") },
];

const CORRECTIVE_STATUS_LABELS = {
  open: uiText("Ожидает создания ПКПД"),
  in_progress: uiText("ПКПД выполняется проверяемой стороной"),
  resolved: uiText("Передано аудитору на проверку"),
  closed: uiText("Выполнение подтверждено аудитором"),
  escalated: uiText("Просрочено, требуется внимание"),
  submitted: uiText("Передано аудитору на проверку"),
  on_verification: uiText("Проверяется аудитором"),
  verification: uiText("Проверяется аудитором"),
  rejected: uiText("Возвращено аудитором на доработку"),
  needs_revision: uiText("Требуется доработка проверяемой стороной"),
};

const CORRECTIVE_STATUS_AUDITEE_LABELS = {
  open: uiText("Работы не начаты"),
  in_progress: uiText("Выполняется"),
  resolved: uiText("Передать аудитору"),
};

const VERIFICATION_META = {
  not_required: { label: uiText("Не требуется (замечаний нет)"), variant: 'neutral' },
  pending: { label: uiText("Ожидает передачи материалов"), variant: 'warning' },
  in_progress: { label: uiText("Проверяется аудитором"), variant: 'info' },
  in_review: { label: uiText("Проверяется аудитором"), variant: 'warning' },
  verified: { label: uiText("Подтверждено аудитором"), variant: 'success' },
  rejected: { label: uiText("Возвращено на доработку"), variant: 'danger' },
};

const ESCALATION_META = {
  none: { label: uiText("Нет"), variant: 'neutral' },
  first_warning: { label: uiText("1-е предупреждение"), variant: 'warning' },
  second_warning: { label: uiText("2-е предупреждение"), variant: 'warning' },
  rights_limited: { label: uiText("Ограничение прав"), variant: 'danger' },
  suspension: { label: uiText("Приостановка"), variant: 'danger' },
  terminated: { label: uiText("Расторжение"), variant: 'danger' },
};

const CLOSURE_META = {
  open: { label: uiText("Открыт"), variant: 'neutral' },
  draft: { label: uiText("Черновик закрытия"), variant: 'neutral' },
  pending: { label: uiText("Готов к закрытию"), variant: 'warning' },
  ready_to_close: { label: uiText("Готов к закрытию"), variant: 'warning' },
  closed: { label: uiText("Формально закрыт"), variant: 'success' },
  closed_by_aas: { label: uiText("Закрыт аудитором"), variant: 'success' },
  closed_by_customer: { label: uiText("Закрыт проверяемым"), variant: 'success' },
  closed_verified: { label: uiText("Закрыт и подтверждён"), variant: 'success' },
};

const ACCESS_STATUS_META = {
  view_only: { label: uiText("Только просмотр"), variant: 'neutral' },
  pending: { label: uiText("Запрос отправлен"), variant: 'warning' },
  approved: { label: uiText("Доступ выдан"), variant: 'success' },
  rejected: { label: uiText("Запрос отклонён"), variant: 'danger' },
  granted: { label: uiText("Можно редактировать"), variant: 'success' },
};

const DOCUMENT_STATUS_META = {
  draft: { label: uiText("Черновик"), variant: 'neutral' },
  generated: { label: uiText("Сформирован"), variant: 'info' },
  agreed: { label: uiText("Согласован"), variant: 'success' },
  sent: { label: uiText("Отправлен"), variant: 'warning' },
  active: { label: uiText("В работе"), variant: 'info' },
  completed: { label: uiText("Завершён"), variant: 'success' },
  issued: { label: uiText("Оформлен"), variant: 'success' },
  signed: { label: uiText("Подписан"), variant: 'success' },
  verified: { label: uiText("Подтверждён"), variant: 'success' },
  open: { label: uiText("Открыт"), variant: 'warning' },
  in_progress: { label: uiText("В работе"), variant: 'info' },
  submitted: { label: uiText("На проверке"), variant: 'warning' },
  resolved: { label: uiText("Решён"), variant: 'success' },
  closed: { label: uiText("Закрыт"), variant: 'success' },
  not_required: { label: uiText("Не требуется"), variant: 'neutral' },
};

const DOCUMENT_TYPE_LABELS = {
  program: uiText("План"),
  act: uiText("Акт / отчёт"),
  report: uiText("Отчёт"),
  prescription: uiText("ПКПД"),
  corrective: uiText("ПКПД"),
  remediation: uiText("Отчёт о выполнении корректирующих действий"),
  registry: uiText("Реестр устранения"),
};

const GENERATION_REASON_LABELS = {
  no_strict_findings: uiText("нет значимых несоответствий"),
  formal_document_not_enabled: uiText("формальный документ ведётся отдельно"),
};

function pickMeta(map, value, fallbackLabel = '—') {
  if (!value) return { label: fallbackLabel, variant: 'neutral' };
  return map[value] || { label: String(value), variant: 'neutral' };
}

function formatGenerationItem(item) {
  const name = DOCUMENT_TYPE_LABELS[item?.type] || item?.type || uiText("Документ");
  if (item?.created) return uiText("{v0}: создан", {v0: (name)});
  if (item?.updated) return uiText("{v0}: обновлён", {v0: (name)});
  if (item?.skipped) return uiText("{v0}: пропущен ({v1})", {v0: (name), v1: (GENERATION_REASON_LABELS[item.reason] || item.reason || uiText("нет данных"))});
  return uiText("{v0}: без изменений", {v0: (name)});
}

const getValidationFromError = (err) => {
  const detail = getApiErrorDetail(err);
  if (detail && typeof detail === 'object' && detail.validation)
    return {message: detail.message||uiText("Есть незавершённые рекомендованные пункты"), validation: detail.validation};
  return null;
};

const mkVM = (src) => {
  const v = src?.validation || src || null;
  if (!v) return null;
  return {
    message: src?.message || null,
    missingRequired: Array.isArray(v.missing_required) ? v.missing_required : [],
    missingRequiredPhotos: Array.isArray(v.missing_required_photos) ? v.missing_required_photos : [],
    missingEvidence: Array.isArray(v.missing_evidence) ? v.missing_evidence : [],
    missingNegativeEvidence: Array.isArray(v.missing_negative_evidence) ? v.missing_negative_evidence : [],
    missingAuditedCompany: Array.isArray(v.missing_audited_company) ? v.missing_audited_company : [],
    openIssuesCount: Number(v.open_issues_count||0),
    canMoveToReady: Boolean(v.can_move_to_ready),
    canComplete: Boolean(v.can_complete),
  };
};

const isQAnswered = questionHasAnswer;


function savePdfBlob(response, filename) {
  const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function DocumentStatusCard({ label, entry, to, onOpen, openLabel, onPdf, createLabel, onGenerate, generateLabel, generationBusy, generationHint }) {
  const exists = !!entry?.exists;
  const statusLabel = pickMeta(DOCUMENT_STATUS_META, entry?.status, entry?.status || uiText("Статус не указан")).label;
  const hasActions = Boolean(to || onOpen || onGenerate || (exists && onPdf));
  return (
    <article className={`workspace-next-step audit-document-card ${exists ? 'is-ready' : 'is-missing'}`}>
      <div className="audit-document-card-header">
        <span className="audit-document-card-icon" aria-hidden="true"><FileText size={18} /></span>
        <div className="audit-document-card-heading">
          <div className="workspace-item-title audit-document-card-title">{label}</div>
          <div className="workspace-muted audit-document-card-subtitle">{entry?.title || uiText("Документ пока не оформлен")}</div>
        </div>
        <Badge variant={exists ? 'success' : 'neutral'}>{exists ? uiText("Есть") : uiText("Нет")}</Badge>
      </div>
      {generationHint ? <div className="workspace-muted audit-document-card-description">{generationHint}</div> : null}
      <div className="audit-document-card-meta">
        <span className={`audit-document-card-status ${exists ? 'is-ready' : 'is-missing'}`}>
          {exists ? <CheckCircle size={14} /> : <Clock size={14} />}
          {statusLabel}
        </span>
        {entry?.date ? <div className="workspace-date-pill"><CalendarDays size={14} /> {fmt(entry.date)}</div> : null}
      </div>
      {hasActions ? <div className="workspace-chip-row audit-document-card-actions">
        {to ? (
          <Link className="btn btn-outline btn-sm" to={to}>{exists ? uiText("Открыть") : (createLabel || uiText("Создать"))}</Link>
        ) : null}
        {!to && onOpen ? (
          <button type="button" className="btn btn-outline btn-sm" onClick={onOpen}>
            {openLabel || (exists ? uiText("Открыть") : (createLabel || uiText("Создать")))}
          </button>
        ) : null}
        {onGenerate ? <button type="button" className="btn btn-outline btn-sm" disabled={generationBusy} onClick={onGenerate}>{generationBusy ? uiText("Формируем…") : (generateLabel || (exists ? uiText("Обновить из аудита") : uiText("Автосформировать")))}</button> : null}
        {exists && onPdf ? <button type="button" className="btn btn-outline btn-sm" onClick={onPdf}>PDF</button> : null}
      </div> : null}
    </article>
  );
}

// Плашка-подтверждение для customer'а (контактного лица проверяемого):
// «Я ознакомлен с актом». По регламенту ААС п. 4.2.3: проверяемому
// направляется проект акта на 3 рабочих дня для замечаний по фактам.
// Это UI-обёртка над POST /api/document-signatures/ с
// role=audited_representative, method=acknowledged.
function CustomerActAcknowledgment({ audit, act, user, onAcknowledged }) {
  const confirm = useConfirm();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    documentSignaturesApi.list(audit.id, 'act')
      .then((res) => { if (alive) setSignatures(res.data || []); })
      .catch(() => { if (alive) setSignatures([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [audit.id, act.id]);

  // Уже подтвердил?
  const alreadySigned = signatures.some((s) =>
    s.role === 'audited_representative' && s.user_id === user?.id && s.document_id === act.id
  );

  const handleAck = async () => {
    if (!await confirm(uiText("Подтвердить ознакомление с актом? Это будет зафиксировано в журнале подписей."), { title: uiText("Ознакомление с актом"), confirmLabel: uiText("Подтвердить"), danger: false })) return;
    setBusy(true);
    try {
      await documentSignaturesApi.create({
        audit_id: audit.id,
        document_kind: 'act',
        document_id: act.id,
        role: 'audited_representative',
        user_id: user.id,
        full_name_text: user.full_name || user.username,
        method: 'acknowledged',
      });
      // обновляем локальный список и зовём onAcknowledged для обновления страницы
      const res = await documentSignaturesApi.list(audit.id, 'act');
      setSignatures(res.data || []);
      if (typeof onAcknowledged === 'function') await onAcknowledged();
    } catch (e) {
      alert(getApiErrorMessage(e) || uiText("Не удалось зарегистрировать ознакомление"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  if (alreadySigned) {
    return (
      <div className="alert" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', marginBottom: 12, fontSize: 13 }}>{uiText("✓ Вы ознакомлены с этим актом. Подтверждение зафиксировано в журнале подписей.")}</div>
    );
  }

  return (
    <div className="alert" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{uiText("Подтвердите ознакомление с актом")}</div>
        <div style={{ fontSize: 13 }}>{uiText("По регламенту вам даётся 3 рабочих дня на замечания по фактологической части. После ознакомления вы можете подать возражения по конкретным пунктам.")}</div>
      </div>
      <button type="button" className="btn btn-primary btn-sm" onClick={handleAck} disabled={busy}>
        {busy ? uiText("Подтверждаем...") : uiText("✓ Я ознакомлен")}
      </button>
    </div>
  );
}

function CustomerProgramAcknowledgment({ audit, program, user, onAcknowledged }) {
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { showToast } = useToast();
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectBusy, setRejectBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    documentSignaturesApi.list(audit.id, 'program')
      .then((res) => { if (alive) setSignatures(res.data || []); })
      .catch(() => { if (alive) setSignatures([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [audit.id, program.id]);

  const alreadySigned = signatures.some((s) =>
    s.role === 'audited_representative' && s.user_id === user?.id && s.document_id === program.id
  );
  const programStatus = String(program?.status || '').toLowerCase();
  const auditStatus = String(audit?.status || '').toLowerCase();
  const isProgramAgreed = alreadySigned
    || ['agreed', 'active', 'completed'].includes(programStatus)
    || ['program_confirmed', 'in_progress', 'ready', 'awaiting_closure', 'closed', 'completed'].includes(auditStatus);
  const isProgramSent = auditStatus === 'program_sent' || programStatus === 'sent';

  const handleAck = async () => {
    if (!await confirm(uiText("Подтвердить ознакомление и согласование плана аудита?"), { title: uiText("Согласование плана"), confirmLabel: uiText("Согласовать"), danger: false })) return;
    setError('');
    setBusy(true);
    try {
      await auditsApi.confirmProgram(audit.id, { accept: true });
      const res = await documentSignaturesApi.list(audit.id, 'program');
      setSignatures(res.data || []);
      if (typeof onAcknowledged === 'function') await onAcknowledged();
    } catch (e) {
      setError(getApiErrorMessage(e) || uiText("Не удалось согласовать план"));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    const reason = await prompt(uiText("Укажите причину отказа от согласования плана аудита."), {
      title: uiText("Отказ от согласования"),
      placeholder: uiText("Причина отказа"),
      confirmLabel: uiText("Отклонить план"),
      required: true,
    });
    if (!reason || !reason.trim()) return;
    setError('');
    setRejectBusy(true);
    try {
      await auditsApi.confirmProgram(audit.id, { accept: false, rejection_reason: reason.trim() });
      if (typeof onAcknowledged === 'function') await onAcknowledged();
    } catch (e) {
      setError(getApiErrorMessage(e) || uiText("Не удалось отклонить план"));
    } finally {
      setRejectBusy(false);
    }
  };

  if (loading) return null;

  if (isProgramAgreed) {
    return (
      <div className="alert" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', marginBottom: 12, fontSize: 13 }}>
        {alreadySigned
          ? uiText("План аудита согласована вами. Подтверждение зафиксировано в журнале подписей.")
          : uiText("План аудита согласована. Сведения о согласовавшем лице доступны в журнале подписей.")}
      </div>
    );
  }

  if (!isProgramSent) {
    return (
      <div className="alert" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color:'var(--premium-text-secondary)', marginBottom: 12, fontSize: 13 }}>{uiText("План аудита подготовлена, но ещё не направлена проверяемой стороне на согласование.")}</div>
    );
  }

  return (
    <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#92400e', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{uiText("План аудита ожидает вашего согласования")}</div>
          <div style={{ fontSize: 13 }}>{uiText("Проверьте область проверки, даты и состав аудиторской группы. После подтверждения аудит перейдёт в работу.")}</div>
          {error ? <div style={{ marginTop: 8, color: '#b91c1c', fontWeight: 600 }}>{error}</div> : null}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={handleReject} disabled={busy || rejectBusy}>
            {rejectBusy ? uiText("Отклоняем...") : uiText("Отказать")}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleAck} disabled={busy || rejectBusy}>
            {busy ? uiText("Согласуем...") : uiText("Согласовать план")}
          </button>
        </div>
      </div>
    </div>
  );
}

function MeetingCard({meeting, canEdit, onEdit, onPdf}) {
  const [open, setOpen] = useState(false);
  const typeLabel = MEETING_TYPE_LABELS[meeting.meeting_type] || meeting.meeting_type;
  const typeColor = {opening:'#1d4ed8',intermediate:'#a16207',closing:'#16a34a'}[meeting.meeting_type]||'#666';
  return (
    <div className="card" style={{marginBottom:10}}>
      <div className="card-body" style={{display:'flex',gap:12,alignItems:'flex-start'}}>
        <div style={{flex:1}}>
          <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:14}}>{typeLabel}</span>
            <span style={{fontSize:12,background:`${typeColor}18`,color:typeColor,borderRadius:999,padding:'2px 8px',fontWeight:600}}>
              {meeting.meeting_date ? String(meeting.meeting_date).slice(0,10) : '—'}
            </span>
          </div>
          {meeting.location && <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}><MapPin size={11} style={{display:'inline',marginRight:3}}/>{meeting.location}</div>}
          {meeting.responsible_name && <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}><Users size={11} style={{display:'inline',marginRight:3}}/>{meeting.responsible_name}</div>}
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0}}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={()=>setOpen(v=>!v)}>{open?<ChevronUp size={14}/>:<ChevronDown size={14}/>}</button>
          <button type="button" className="btn btn-outline btn-sm" onClick={()=>onPdf(meeting.id)}>PDF</button>
          {canEdit && <button type="button" className="btn btn-outline btn-sm" onClick={()=>onEdit(meeting)}>{uiText("Изменить")}</button>}
        </div>
      </div>
      {open && (
        <div className="card-body" style={{borderTop:'1px solid #f0f0f0',fontSize:13}}>
          {meeting.participants?.length>0 && <div style={{marginBottom:8}}><strong>{uiText("Участники:")}</strong><ul style={{margin:'4px 0 0 16px',padding:0}}>{meeting.participants.map((p,i)=><li key={i}>{p}</li>)}</ul></div>}
          {meeting.agenda?.length>0 && <div style={{marginBottom:8}}><strong>{uiText("Повестка:")}</strong><ol style={{margin:'4px 0 0 16px',padding:0}}>{meeting.agenda.map((a,i)=><li key={i}>{a}</li>)}</ol></div>}
          {meeting.decisions?.length>0 && <div style={{marginBottom:8}}><strong>{uiText("Решения:")}</strong><ol style={{margin:'4px 0 0 16px',padding:0}}>{meeting.decisions.map((d,i)=><li key={i}>{d}</li>)}</ol></div>}
          {meeting.note && <div><strong>{uiText("Примечание:")}</strong> {meeting.note}</div>}
        </div>
      )}
    </div>
  );
}

export default function AuditDetailRoute() {
  const { id } = useParams();
  const { user } = useAuth();
  return <AuditDetailPage key={`${id}:${user?.id}:${user?.role}`} />;
}

function AuditDetailPage() {
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { showToast } = useToast();
  const {id} = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {user} = useAuth();

  const [audit, setAudit] = useState(null);
  const [technicalData, setTechnicalData] = useState(null), [technicalFocus, setTechnicalFocus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Сводка по индексу соответствия (КН/ЗН/НН + балл).
  // Подгружается с эндпоинта /api/audits/:id/compliance-summary.
  // Используется для плашки в шапке аудита и при финализации акта.
  const [complianceSummary, setComplianceSummary] = useState(null);
  // Модалка приостановки аудита. По регламенту ААС п. 4.4.4 — эскалационный
  // кейс при отказе в доступе или форс-мажорах.
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendUntil, setSuspendUntil] = useState('');
  const [suspendBusy, setSuspendBusy] = useState(false);
  const initialTab = searchParams.get('tab') || 'checklist';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [overviewOpen, setOverviewOpen] = useState(false);
  useEffect(() => { setOverviewOpen(false); }, [id]);
  useEffect(() => { if (audit?.id) rememberAudit(user?.id, audit.id); }, [audit?.id, user?.id]);
  const answerBuffer = useDraftBuffer(user?.id, id, 'checklist');
  const expectationBuffer = useDraftBuffer(user?.id, id, 'expectations');
  const answers = answerBuffer.values;
  const expectedAnswers = expectationBuffer.values;
  const [expectedSaving, setExpectedSaving] = useState({});
  const [defaultExpectedDocumentId, setDefaultExpectedDocumentId] = useState(null);
  const [expectedAnswerQuestion, setExpectedAnswerQuestion] = useState(null);
  const [expectedAnswerGenerating, setExpectedAnswerGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const issueBuffer = useDraftBuffer(user?.id, id, 'issue');
  const emptyIssueForm = {title:'',description:'',requirement_ref:'',priority:'minor',question_id:'',due_date:''};
  const issueForm = issueBuffer.values.form || emptyIssueForm;
  const [issueDateValid, setIssueDateValid] = useState(true);
  const setIssueForm = value => issueBuffer.edit('form', typeof value === 'function' ? value(issueBuffer.values.form || emptyIssueForm) : value);
  const [issueMessages, setIssueMessages] = useState({});
  const commentBuffer = useDraftBuffer(user?.id, id, 'audit-comment');
  const comment = commentBuffer.values.text || '';
  const setComment = value => commentBuffer.edit('text', value);
  const [commentSaving, setCommentSaving] = useState(false);
  const [commentSaveError, setCommentSaveError] = useState('');
  const commentSaveRef = useRef(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [checklistUploadCount, setChecklistUploadCount] = useState(0);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [showReportExport, setShowReportExport] = useState(false);
  const [questionToRemove, setQuestionToRemove] = useState(null);
  useEffect(() => { setShowReportExport(false); setQuestionToRemove(null); }, [id, user?.id]);
  const [showExtraForm, setShowExtraForm] = useState(false);
  const [extraForm, setExtraForm] = useState({section_title:'Дополнительные вопросы',text:'',question_type:'text',options_raw:'',required:false,answer_scope:'auditor',allow_comment:false,allow_photo:false});
  const [draftState, setDraftState] = useState({dirty:false,saving:false,savedAt:null,serverCount:0});
  const [saveError, setSaveError] = useState('');
  const [answerConflicts, setAnswerConflicts] = useState([]);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  useEffect(() => { if (answerConflicts.length) setConflictDialogOpen(true); }, [answerConflicts]);
  const [retryRevision, setRetryRevision] = useState(0);
  const [issueSaving, setIssueSaving] = useState(false);
  const [statusBlocker, setStatusBlocker] = useState(null);
  const [statusChanging, setStatusChanging] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [procedureSummary, setProcedureSummary] = useState(null);
  const [procedureControl, setProcedureControl] = useState(null);
  const [procedureWorkflow, setProcedureWorkflow] = useState(null);
  const [accessRequests, setAccessRequests] = useState([]);
  const [documentBundle, setDocumentBundle] = useState({});
  const [generationState, setGenerationState] = useState({ busy:false, type:null, result:null });
  const [accessRequestBusy, setAccessRequestBusy] = useState(false);
  const [procedureBusy, setProcedureBusy] = useState(false);
  const [orderBusy, setOrderBusy] = useState(false);

  const [meetings, setMeetings] = useState([]);
  const meetingDraft=useRecordFormDraft(user?.id,id,'meeting',EMPTY_MEETING_FORM);
  const {buffer:meetingBuffer,form:meetingForm,setForm:setMeetingForm,recordId:meetingId}=meetingDraft;
  const editingMeeting=meetingId ? {id:meetingId} : null;
  const [meetingFormOpen, setMeetingFormOpen] = useState(false);
  const [meetingSaving, setMeetingSaving] = useState(false);
  const [meetingSaveError,setMeetingSaveError]=useState('');
  const meetingSaveRef=useRef(false);
  useEffect(()=>{setMeetingFormOpen(false);setMeetingSaveError('');},[id,user?.id]);
  useEffect(()=>{if(meetingBuffer.count) setMeetingFormOpen(true);},[meetingBuffer,meetingBuffer.count]);

  const [correctiveActions, setCorrectiveActions] = useState([]);
  const caBuffer = useDraftBuffer(user?.id, id, 'corrective-action');
  const emptyCA = {title:'',description:'',due_date:'',responsible_name:'',evidence_comment:'',status:'open'};
  const caForm = caBuffer.values.form || emptyCA;
  const setCAForm = value => caBuffer.edit('form', typeof value === 'function' ? value(caBuffer.values.form || emptyCA) : value);
  const caSaveRef = useRef(false);
  const [caFormOpen, setCAFormOpen] = useState(false);
  const [caSaving, setCASaving] = useState(false);
  const [caSaveError, setCASaveError] = useState('');

  const fileRef = useRef();
  const saveQueue = useMemo(() => createSaveQueue(), [id, user?.id]);
  const expectedQueue = useMemo(() => createSaveQueue(), [id, user?.id]);
  const commitRef = useRef(null);
  const issueSaveRef = useRef(false);
  const loadSequence = useRef(0);
  const statusFeedbackRef = useRef(null);
  const tabsRef = useRef(null);
  const checklistRef = useRef(null);
  const checklistWorkbenchRef = useRef(null);
  const issuesRef = useRef(null);
  const meetingsRef = useRef(null);
  const documentsRef = useRef(null);
  const historyRef = useRef(null);

  const isAdmin = user?.role==='admin';
  const isAuditor = user?.role==='auditor';
  const isCustomer = isAuditeeRole(user);
  const [classifierRefresh, setClassifierRefresh] = useState(0);

  const procedureTimeline = procedureSummary?.stage_timeline || [];
  const procedureActions = procedureSummary?.next_actions || [];
  const procedureDocuments = procedureSummary?.documents || {};
  const documentCardKeys = ['order', 'program', 'act', 'corrective', 'remediation', 'registry'];
  const visibleDocumentCount = documentCardKeys.filter((key) => procedureDocuments[key]?.exists).length;
  const documentCounts = {
    objections: Array.isArray(documentBundle.objections) ? documentBundle.objections.length : 0,
  };
  const generationSummary = generationState.result?.summary || null;
  const generationProgramId = (
    (generationState.result?.results || []).find((item) => item?.type === 'program' && item?.id)?.id
    || generationState.result?.bundle?.program?.id
    || null
  );
  const actFindings = Array.isArray(documentBundle.act?.findings) ? documentBundle.act.findings : [];
  const objectionCountByTarget = useMemo(() => {
    const bucket = {};
    (documentBundle.objections || []).forEach((item) => {
      const ref = item?.target?.ref;
      if (!ref) return;
      bucket[ref] = (bucket[ref] || 0) + 1;
    });
    return bucket;
  }, [documentBundle.objections]);

  const load = useCallback(async () => {
    const request = ++loadSequence.current;
    try {
      setLoadError('');
      const [auditRes, draftsRes, complianceRes, expectedRes] = await Promise.all([
        auditsApi.get(id),
        auditsApi.getDrafts(id).catch(()=>({data:{items:[]}})),
        auditsApi.complianceSummary(id).catch(()=>({data:null})),
        (isAdmin || isAuditor) ? auditsApi.expectedAnswers(id).catch(()=>({data:{items:[],default_document_id:null}})) : Promise.resolve({data:{items:[],default_document_id:null}}),
      ]);
      if (request !== loadSequence.current) return;
      setComplianceSummary(complianceRes?.data || null);
      const procedureRes = await workspaceApi.auditProcedure(id).catch(()=>({data:null}));
      if (request !== loadSequence.current) return;
      setAudit(auditRes.data);
      setProcedureSummary(procedureRes?.data || null);
      setStatusBlocker(null);
      const draftItems = draftsRes?.data?.items||[];
      const ansMap = selectChecklistAnswers(auditRes.data.answers || [], draftItems, isAuditeeRole(user) ? 'customer' : 'auditor');
      answerBuffer.hydrate(ansMap);
      const expectedMap = {};
      (expectedRes?.data?.items || []).forEach((item) => { expectedMap[item.frontend_key] = item; });
      expectationBuffer.hydrate(expectedMap);
      setDefaultExpectedDocumentId(expectedRes?.data?.default_document_id || null);
      setDraftState(p=>({...p,serverCount:draftItems.length}));

      const [mtRes, caRes, docsRes, controlRes, workflowRes, accessRes] = await Promise.all([
        auditMeetingsApi.list({audit_id:id}).catch(()=>({data:[]})),
        correctiveActionsApi.list({audit_id:id}).catch(()=>({data:[]})),
        workspaceApi.auditDocuments(id).catch(()=>({data:{}})),
        procedureControlApi.get(id).catch(()=>({data:null})),
        procedureWorkflowApi.getByAudit(id).catch(()=>({data:null})),
        auditsApi.listEditAccessRequests(id).catch(()=>({data:[]})),
      ]);
      if (request !== loadSequence.current) return;
      setMeetings(Array.isArray(mtRes.data)?mtRes.data:Array.isArray(mtRes.data?.items)?mtRes.data.items:[]);
      setCorrectiveActions(Array.isArray(caRes.data)?caRes.data:Array.isArray(caRes.data?.items)?caRes.data.items:[]);
      setProcedureControl(controlRes?.data || null);
      setProcedureWorkflow(workflowRes?.data || null);
      setAccessRequests(Array.isArray(accessRes?.data) ? accessRes.data : []);
      setDocumentBundle(docsRes?.data || {});
    } catch (e) {
      if (request !== loadSequence.current) return;
      console.error(e);
      setLoadError(getApiErrorMessage(e));
    }
    setLoading(false);
  }, [id, isAdmin, isAuditor, answerBuffer, expectationBuffer, user?.role]);

  useEffect(()=>{load(); return () => { loadSequence.current += 1; };},[load]);

  const changeExpectedAnswer = useCallback((question, value) => {
    const key = questionFrontendKey(question);
    const current = expectationBuffer.values[key] || {};
    expectationBuffer.edit(key, { expected_answer: value, source_document_id: current.source_document_id || null,
      source_document: current.source_document, saved: false });
  }, [expectationBuffer]);

  const saveExpectedAnswer = useCallback(async (question) => {
    const key = questionFrontendKey(question);
    return expectedQueue(async () => {
      if (!expectationBuffer.active) return;
      const entry = expectationBuffer.snapshot()[key];
      if (!entry) return;
      const current = entry.value;
      setExpectedSaving(items => ({ ...items, [key]: true }));
      try {
      const response = await auditsApi.saveExpectedAnswer(id, {
        question_id: question.extra_question_id ? null : (question.question_id || question.id),
        extra_question_id: question.extra_question_id || null,
        expected_answer: current.expected_answer || null,
        source_document_id: current.source_document_id || null,
      });
      expectationBuffer.acknowledge({ [key]: entry }, { committed: true, values: { [key]: { ...response.data, saved: true } } });
    } catch (requestError) {
      showToast(getApiErrorMessage(requestError) || uiText("Не удалось сохранить ожидаемый ответ"), 'error');
    } finally {
      setExpectedSaving(items => ({ ...items, [key]: false }));
    }
    });
  }, [expectationBuffer, expectedQueue, id, showToast]);

  const generateExpectedAnswer = useCallback(async (documentId) => {
    if (!expectedAnswerQuestion) return;
    const key = questionFrontendKey(expectedAnswerQuestion);
    const beforeGeneration = expectationBuffer.snapshot();
    setExpectedAnswerGenerating(true);
    try {
      // Manual saves and explicit generation write the same record. Do not let
      // an older blur-save reach the server after the generated answer.
      await expectedQueue(async () => {
      if (!expectationBuffer.active) return;
      const response = await auditsApi.generateExpectedAnswer(id, {
        question_id: expectedAnswerQuestion.extra_question_id ? null : (expectedAnswerQuestion.question_id || expectedAnswerQuestion.id),
        extra_question_id: expectedAnswerQuestion.extra_question_id || null,
        document_id: documentId,
      });
      // Generation is explicit. Keep a manual revision typed during the request.
      expectationBuffer.acknowledge(beforeGeneration[key] ? { [key]: beforeGeneration[key] } : {}, { committed: true, values: { [key]: { ...response.data, saved: true } } });
      expectationBuffer.hydrate({ ...expectationBuffer.values, [key]: { ...response.data, saved: true } });
      setDefaultExpectedDocumentId(response.data.default_document_id || documentId);
      setExpectedAnswerQuestion(null);
      showToast(uiText("Ожидаемый ответ подготовлен. Его можно отредактировать вручную."), 'success');
      });
    } catch (requestError) {
      showToast(getApiErrorMessage(requestError) || uiText("Не удалось сформировать ожидаемый ответ"), 'error');
    } finally {
      setExpectedAnswerGenerating(false);
    }
  }, [expectedAnswerQuestion, id, showToast, expectationBuffer, expectedQueue]);

  const teamAssignments = audit?.assignments || [];
  const myAssignment = teamAssignments.find(a => a.auditor_user_id === user?.id) || null;
  const ownAccessRequest = accessRequests.find((item) => item.requester_user_id === user?.id) || null;
  const canManageAccess = false;
  const pendingAccessRequests = [];
  const isAcceptedLead = teamAssignments.some(a => a.auditor_user_id === user?.id && a.status === 'accepted' && a.team_role === 'lead');
  const canManageTeam = isAdmin || (isAuditor && (user?.id===audit?.auditor?.id || isAcceptedLead));
  const canEdit = isAdmin || user?.id===audit?.auditor?.id || teamAssignments.some(a => a.auditor_user_id === user?.id && a.status === 'accepted');
  const canGenerateBundle = canEdit;
  const canParticipate = canEdit || isCustomer || user?.id===audit?.customer?.id;
  const auditLocked = ['completed', 'closed'].includes(String(audit?.status || '').toLowerCase());
  const checklistQuestions = useMemo(
    () => (audit?.template?.sections || []).flatMap((section) => section.questions || []),
    [audit],
  );
  const answerInputsEnabled = canParticipate && !auditLocked;
  const canAnswerChecklist = checklistQuestions.some((question) => {
    const scope = question.effective_answer_scope || question.answer_scope || 'auditor';
    return canAnswerAuditQuestion(user, scope, answerInputsEnabled);
  });
  const canManageCorrectivePlan = canEdit || isCustomer;
  const canDeleteAttachment = useCallback((file) => (
    !auditLocked
    && (canEdit || (canParticipate && Number(file?.uploaded_by_id) === Number(user?.id)))
  ), [auditLocked, canEdit, canParticipate, user?.id]);

  const allowedTabs = useMemo(() => new Set([
    'checklist','issues','meetings','corrective','comments','files','documents','history',
    ...(canManageTeam ? ['admin'] : []),
  ]), [canManageTeam]);

  const sectionRefs = useMemo(() => ({
    checklist: checklistRef,
    issues: issuesRef,
    meetings: meetingsRef,
    documents: documentsRef,
    history: historyRef,
  }), []);

  const openTab = useCallback((tab, options = {}) => {
    const nextTab = allowedTabs.has(tab) ? tab : 'checklist';
    setActiveTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'checklist') next.delete('tab');
    else next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
    if (options.scroll) {
      setTimeout(() => {
        const ref = sectionRefs[nextTab]?.current || tabsRef.current;
        ref?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [allowedTabs, searchParams, setSearchParams, sectionRefs]);

  const resolveInternalTarget = useCallback((target, fallbackTab = 'documents') => {
    if (!target || typeof target !== 'string') return { tab: fallbackTab, route: null, anchor: null };
    if (target.startsWith('?tab=')) return { tab: target.replace('?tab=', ''), route: null, anchor: null };
    const safeTarget = safeInternalRoute(target, '');
    if (!safeTarget) return { tab: fallbackTab, route: null, anchor: null };
    const sameAuditPrefix = `/audits/${id}`;
    if (safeTarget.startsWith(sameAuditPrefix)) {
      const suffix = safeTarget.slice(sameAuditPrefix.length);
      if (!suffix || suffix === '/') return { tab: 'checklist', route: null, anchor: null };
      if (suffix.startsWith('?')) {
        const params = new URLSearchParams(suffix.slice(1));
        return { tab: params.get('tab') || fallbackTab, route: null, anchor: params.get('anchor') || null };
      }
      return { tab: fallbackTab, route: safeTarget, anchor: null };
    }
    if (safeTarget.startsWith('/')) return { tab: null, route: safeTarget, anchor: null };
    return { tab: fallbackTab, route: null, anchor: null };
  }, [id]);

  const openActionTarget = useCallback((target, fallbackTab = 'documents') => {
    const resolved = resolveInternalTarget(target, fallbackTab);
    if (resolved.route) {
      navigate(resolved.route);
      return;
    }
    if (resolved.tab) {
      openTab(resolved.tab, { scroll: true });
      return;
    }
    openTab(fallbackTab, { scroll: true });
  }, [navigate, openTab, resolveInternalTarget]);

  useEffect(()=>{
    const tabFromQuery = searchParams.get('tab') || 'checklist';
    const safeTab = allowedTabs.has(tabFromQuery) ? tabFromQuery : 'checklist';
    if (safeTab !== activeTab) setActiveTab(safeTab);
  }, [searchParams, allowedTabs]);

  const unsyncedRevision = Object.entries(answerBuffer.snapshot({ unsyncedOnly: true })).map(([key, entry]) => `${key}:${entry.revision}`).join('|');
  useEffect(() => {
    if (!audit || loading || !canAnswerChecklist || !unsyncedRevision || answerBuffer.recovery || answerConflicts.length) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      saveQueue(async () => {
        if (cancelled) return;
        const snapshot = answerBuffer.snapshot({ unsyncedOnly: true });
        const payload = buildScopedAnswerPayload({ answers: snapshotValues(snapshot), questions: checklistQuestions, user, enabled: answerInputsEnabled });
        if (!payload.length) return;
        setDraftState(p => ({ ...p, saving: true }));
        try {
          const res = await auditsApi.saveDrafts(id, payload);
          const sentKeys = new Set(payload.map(questionFrontendKey));
          answerBuffer.acknowledge(Object.fromEntries(Object.entries(snapshot).filter(([key]) => sentKeys.has(key))));
          answerBuffer.rebaseAnswerRevisions(snapshot, res?.data?.answer_revisions);
          setSaveError('');
          setDraftState(p => ({ ...p, savedAt: res?.data?.saved_at || new Date().toISOString() }));
        } catch (error) { setSaveError(getApiErrorMessage(error)); if (error.response?.data?.detail?.code === 'answer_conflict') setAnswerConflicts(error.response.data.detail.conflicts || []); }
        finally { setDraftState(p => ({ ...p, saving: false })); }
      });
    }, 1200);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [unsyncedRevision, retryRevision, audit, loading, canAnswerChecklist, answerBuffer, saveQueue, id, checklistQuestions, user, answerInputsEnabled, answerConflicts.length]);
  useEffect(() => {
    const retry = () => setRetryRevision(value => value + 1);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  const allQuestions = useMemo(()=>(
    (audit?.template?.sections||[]).flatMap(s=>(s.questions||[]).map(q=>({...q,sectionTitle:s.title})))
  ),[audit]);

  const answeredQ = useMemo(()=>{
    const af=audit?.files||[];
    return allQuestions.filter(q=>isQAnswered(q,answers,af)).length;
  },[allQuestions,answers,audit]);

  const openIssues = (audit?.issues||[]).filter(i=>['open','in_progress'].includes(i.status)).length;
  const vv = mkVM(statusBlocker||audit?.validation);

  const missingRequiredItems = useMemo(()=>{
    const af=audit?.files||[];
    return allQuestions.filter(q=>q.required&&!isQAnswered(q,answers,af)).map(q=>({
      ...q,
      frontend_key: q.frontend_key||(q.extra_question_id?`extra-${q.extra_question_id}`:`q-${q.question_id||q.id}`),
      question_text: q.text,
      section_title: q.sectionTitle,
    }));
  },[allQuestions,answers,audit]);

  const missingKeys = useMemo(()=>new Set(missingRequiredItems.map(q=>q.frontend_key).filter(Boolean)),[missingRequiredItems]);

  const negativeAnsweredQuestions = useMemo(()=>allQuestions.filter(q=>{
    if (q.is_extra) return false;
    const key = q.frontend_key||(q.extra_question_id?`extra-${q.extra_question_id}`:`q-${q.question_id||q.id}`);
    const ans = answers[key];
    return (q.question_type === 'yesno' && ans?.value_bool === false) || q.qualification_rule?.nonconforming_options?.includes(ans?.value_text);
  }),[allQuestions,answers]);

  const handleAnswerChange = (question,field,value)=>{
    const key = question.frontend_key||(question.extra_question_id?`extra-${question.extra_question_id}`:`q-${question.question_id||question.id}`);
    const previous = answerBuffer.values[key] || {};
    answerBuffer.edit(key, { value_text: previous.value_text, value_number: previous.value_number, value_bool: previous.value_bool, comment_text: previous.comment_text,
      expected_answer_revision: Object.prototype.hasOwnProperty.call(previous,'expected_answer_revision') ? previous.expected_answer_revision : previous.answered_by && previous.answered_by !== (isCustomer ? 'customer' : 'auditor') ? null : previous.answer_revision ?? null,
      frontend_key:key,question_id:question.question_id||(!question.is_extra?(question.id||null):null),extra_question_id:question.extra_question_id||(question.is_extra?(question.id||null):null),[field]:value });
  };

  const openIssueForQuestion = (question, section) => {
    const title = String(question?.text || 'Несоответствие по пункту чек-листа').trim();
    setIssueForm((previous) => ({
      ...previous,
      title: previous.title || `Несоответствие: ${title.slice(0, 110)}`,
      description: previous.description || (question?.qualification_rule ? `Зафиксирована оценка ниже порога критерия «${title}».` : `Зафиксирован ответ «Нет» по пункту чек-листа «${title}».`),
      requirement_ref: previous.requirement_ref || question?.qualification_rule?.requirement_ref || section?.title || '',
      question_id: question?.is_extra ? '' : String(question?.question_id || question?.id || ''),
    }));
    openTab('issues', { scroll: true });
  };

  const commitAnswers = () => {
    if (commitRef.current) return commitRef.current;
    if (answerBuffer.recovery) return Promise.reject(new Error(uiText("Сначала восстановите черновик или оставьте данные сервера.")));
    if (answerConflicts.length) return Promise.reject(new Error(uiText("Сначала сравните варианты изменённых ответов.")));
    commitRef.current = saveQueue(async () => {
      if (!answerBuffer.active) throw new Error(uiText("Страница аудита уже закрыта. Вернитесь в аудит и проверьте черновик."));
      const snapshot = answerBuffer.snapshot();
      // Do not resubmit untouched final answers belonging to other editors.
      const answeredBy = isAuditeeRole(user) ? 'customer' : 'auditor';
      const drafts = Object.fromEntries(Object.entries(answerBuffer.values).filter(([, value]) => value.is_draft && value.answered_by === answeredBy));
      const payload = buildScopedAnswerPayload({ answers: { ...drafts, ...snapshotValues(snapshot) }, questions: checklistQuestions, user, enabled: answerInputsEnabled });
      if (payload.length) {
        const result = await auditsApi.submitAnswers(id, payload);
        const sentKeys = new Set(payload.map(questionFrontendKey));
        answerBuffer.acknowledge(Object.fromEntries(Object.entries(snapshot).filter(([key]) => sentKeys.has(key))), { committed: true });
        answerBuffer.rebaseAnswerRevisions(snapshot, result?.data?.answer_revisions);
      }
      setSaveError('');
      setDraftState(p => ({ ...p, savedAt: new Date().toISOString(), serverCount: 0 }));
    }).catch(error => {
      if (error.response?.data?.detail?.code === 'answer_conflict') setAnswerConflicts(error.response.data.detail.conflicts || []);
      throw error;
    }).finally(() => { commitRef.current = null; });
    return commitRef.current;
  };

  const handleSave = async()=>{
    setSaving(true);
    try {
      await commitAnswers();
      await load();
    } catch(e){setSaveError(getApiErrorMessage(e));}
    setSaving(false);
  };

  const handleStatusChange = async(newStatus)=>{
    try {
      if (statusChanging) return;
      setStatusChanging(newStatus);
      setStatusBlocker(null);
      await commitAnswers();
      const completedAudit = audit;
      const response = await auditsApi.update(id,{status:newStatus});
      const persistedStatus = response?.data?.status;
      const expectedStatuses = newStatus === 'ready'
        ? ['ready', 'awaiting_closure']
        : ['completed', 'closed'].includes(newStatus)
          ? ['completed', 'closed']
          : [newStatus];
      if (!expectedStatuses.includes(persistedStatus)) {
        throw new Error(uiText("Сервер не подтвердил изменение статуса. Обновите страницу и повторите действие."));
      }
      await load();
      showToast({
        type: 'success',
        message: newStatus === 'ready'
          ? uiText("Полевой этап завершён")
          : newStatus === 'in_progress'
            ? uiText("Аудит переведён в работу")
            : uiText("Статус аудита обновлён"),
        detail: newStatus === 'ready'
          ? uiText("Аудит переведён в статус «Готов к закрытию».")
          : undefined,
      });
      if (['completed', 'closed'].includes(newStatus) && completedAudit?.meta?.audited_company?.id) {
        const baseDate = completedAudit.scheduled_date ? new Date(completedAudit.scheduled_date) : new Date();
        baseDate.setFullYear(baseDate.getFullYear() + 1);
        const defaultDate = [
          baseDate.getFullYear(),
          String(baseDate.getMonth() + 1).padStart(2, '0'),
          String(baseDate.getDate()).padStart(2, '0'),
        ].join('-');
        const nextDate = await prompt(
          uiText("Аудит завершён. Укажите ориентировочную дату следующего регулярного аудита или нажмите «Не сейчас»."),
          {
            title: uiText("Запланировать следующий аудит"),
            confirmLabel: uiText("Перейти к планированию"),
            cancelLabel: uiText("Не сейчас"),
            inputType: 'date',
            defaultValue: defaultDate,
            required: true,
          },
        );
        if (nextDate) {
          const company = completedAudit.meta.audited_company;
          const params = new URLSearchParams({
            audited_company_id: String(company.id),
            scheduled_date: `${nextDate}T10:00`,
            title: `Повторный аудит — ${company.name || completedAudit.title}`,
          });
          navigate(`/audits/new?${params.toString()}`);
        }
      }
    } catch(e){
      const b=getValidationFromError(e);
      if(b){
        setStatusBlocker({...b,targetStatus:newStatus});
        window.requestAnimationFrame(() => statusFeedbackRef.current?.scrollIntoView({behavior:'smooth',block:'center'}));
        return;
      }
      showToast({type:'error',message:uiText("Не удалось изменить статус аудита"),detail:getApiErrorMessage(e)});
    } finally {
      setStatusChanging(null);
    }
  };

  const handleComment = async()=>{
    if(!comment.trim() || commentBuffer.recovery || commentSaveRef.current)return;
    commentSaveRef.current=true;setCommentSaving(true);setCommentSaveError('');
    const snapshot=commentBuffer.snapshot();
    try{await commentsApi.create({audit_id:parseInt(id,10),text:comment});commentBuffer.acknowledge(snapshot,{committed:true});commentBuffer.hydrate({text:''});await load();}
    catch(e){const message=getApiErrorMessage(e);setCommentSaveError(message);alert(message);}
    finally{commentSaveRef.current=false;setCommentSaving(false);}
  };

  const handleFileUpload = async(e)=>{
    const file=e.target.files?.[0];if(!file)return;
    try{await filesApi.upload({auditId:parseInt(id,10),file});await load();}
    catch(e){alert(getApiErrorMessage(e));}
  };

  const handleFileDelete = async(file)=>{
    if (!file?.id || !canDeleteAttachment(file)) return;
    const fileName = file.original_name || file.filename || uiText("Файл #{v0}", {v0: (file.id)});
    const approved = await confirm(
      uiText("Удалить «{v0}»? Файл исчезнет из аудита и базы знаний. Запись об удалении останется в журнале действий.", {v0: (fileName)}),
      {title:uiText("Удаление вложения"),confirmLabel:uiText("Удалить файл"),danger:true},
    );
    if (!approved) return;
    setDeletingFileId(file.id);
    try {
      await filesApi.delete(file.id);
      await load();
      showToast({type:'success',message:uiText("Файл удалён"),detail:fileName});
    } catch(e) {
      showToast({type:'error',message:uiText("Не удалось удалить файл"),detail:getApiErrorMessage(e)});
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleQPhoto = async(question,file)=>{
    if(!file)return;
    const pk=question.frontend_key||(question.extra_question_id?`extra-${question.extra_question_id}`:`q-${question.question_id||question.id}`);
    setChecklistUploadCount(count => count + 1);
    try {
      await filesApi.upload({auditId:parseInt(id,10),questionId:question.question_id||null,extraQuestionId:question.extra_question_id||null,file,
        onUploadProgress:(evt)=>{if(evt.total)setUploadProgress(p=>({...p,[pk]:Math.round(evt.loaded/evt.total*100)}));}});
      setUploadProgress(p=>({...p,[pk]:100}));await load();
      setTimeout(()=>setUploadProgress(p=>({...p,[pk]:0})),800);
    } catch(e){setUploadProgress(p=>({...p,[pk]:0}));alert(getApiErrorMessage(e));}
    finally { setChecklistUploadCount(count => Math.max(0, count - 1)); }
  };

  const handleIssueEvidenceUpload = async(issue,file)=>{
    if(!file)return;
    try {
      await filesApi.upload({auditId:parseInt(id,10),issueId:issue.id,file,purpose:'remediation_evidence'});
      await load();
    } catch(e){alert(getApiErrorMessage(e));}
  };

  const handleQuestionRemoval = async (question, reason) => {
    if (!isAdmin) throw new Error(uiText("Удаление вопросов доступно только администратору."));
    // Drain in-flight answer/expectation writes before the audit-scoped removal.
    // Never discard a local edit or submit answers as a side effect of deleting.
    await saveQueue(() => expectedQueue(async () => {
      const key = questionFrontendKey(question);
      if (answerBuffer.recovery || expectationBuffer.recovery
          || answerBuffer.snapshot({ unsyncedOnly: true })[key]
          || expectationBuffer.snapshot()[key]
          || answerConflicts.length) {
        throw new Error(uiText("Сначала сохраните изменения этого вопроса и разрешите конфликт или восстановленный черновик. Затем повторите удаление."));
      }
      if (question.extra_question_id || question.is_extra) {
        await auditsApi.removeExtraQuestion(id, question.extra_question_id || question.id, reason);
      } else {
        await auditsApi.removeQuestion(id, question.question_id || question.id, reason);
      }
      // The backend archives saved drafts too. Clear only the acknowledged
      // local revision of this question; unrelated form drafts remain intact.
      const snapshot = answerBuffer.snapshot();
      if (snapshot[key]) answerBuffer.acknowledge({ [key]: snapshot[key] }, { committed: true });
      setQuestionToRemove(null);
      await load();
      showToast({ type: 'success', message: uiText("Вопрос удалён только из этого чек-листа"), detail: uiText("Ответы и вложения сохранены в аудите. Общий шаблон не изменён.") });
    }));
  };

  // Приостановка аудита по регламенту п. 4.4.4 ААС.
  // Срабатывает в кейсах: отказ в доступе, форс-мажоры, длительная неготовность
  // проверяемого. После suspend аудит блокируется до возобновления — правки
  // ответов и замечаний невозможны.
  const handleSuspend = async () => {
    if (!suspendReason.trim()) {
      alert(uiText("Укажите причину приостановки"));
      return;
    }
    setSuspendBusy(true);
    try {
      await auditsApi.suspend(id, {
        reason: suspendReason.trim(),
        until: suspendUntil || null,
      });
      setShowSuspendModal(false);
      setSuspendReason('');
      setSuspendUntil('');
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setSuspendBusy(false);
    }
  };

  const handleResume = async () => {
    if (!await confirm(uiText("Возобновить аудит? Аудит вернётся в статус «В работе»."), { title: uiText("Возобновление аудита"), confirmLabel: uiText("Возобновить"), danger: false })) return;
    try {
      await auditsApi.resume(id);
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    }
  };

  const handleCreateIssue = async()=>{
    if(!issueForm.title.trim())return alert(uiText("Введите заголовок"));
    if (!issueDateValid) return alert(uiText("Завершите ввод срока в формате ДД.ММ.ГГГГ или очистите поле."));
    if (issueBuffer.recovery) return;
    if (issueSaveRef.current) return;
    issueSaveRef.current = true;
    setIssueSaving(true);
    const issueSnapshot = issueBuffer.snapshot();
    try{
      await commitAnswers();
      await issuesApi.create({audit_id:parseInt(id,10),question_id:issueForm.question_id?Number(issueForm.question_id):null,title:issueForm.title,description:issueForm.description,requirement_ref:issueForm.requirement_ref,priority:issueForm.priority,classification:issueForm.priority,due_date:issueForm.due_date||null});
      issueBuffer.acknowledge(issueSnapshot, { committed: true });
      issueBuffer.hydrate({ form: emptyIssueForm });await load();}
    catch(e){setSaveError(getApiErrorMessage(e));alert(getApiErrorMessage(e));}
    finally { issueSaveRef.current = false; setIssueSaving(false); }
  };

  const handleIssueStatus = async(issueId,status)=>{
    try{await issuesApi.update(issueId,{status});await load();}catch(e){alert(getApiErrorMessage(e));}
  };

  const handleIssueMsg = async(issueId)=>{
    const text=(issueMessages[issueId]||'').trim();if(!text)return;
    try{await issuesApi.addMessage(issueId,{text});setIssueMessages(p=>({...p,[issueId]:''}));await load();}
    catch(e){alert(getApiErrorMessage(e));}
  };

  const handleSaveMeeting = async()=>{
    if(meetingSaveRef.current || meetingBuffer.recovery || !canEdit) return;
    if(meetingId && !meetings.some(m=>String(m.id)===String(meetingId))) {
      setMeetingSaveError(uiText("Этот протокол больше не доступен в данном аудите. Черновик сохранён; создание новой записи автоматически не выполняется."));return;
    }
    if(!isSafeIsoDate(meetingForm.meeting_date))return alert(uiText("Укажите корректную дату совещания в диапазоне 1900–2100"));
    meetingSaveRef.current=true;
    setMeetingSaving(true);
    setMeetingSaveError('');
    const snapshot=meetingBuffer.snapshot();
    try {
      const payload={...meetingForm,audit_id:Number(id),participants:meetingForm.participants.split('\n').filter(Boolean),agenda:meetingForm.agenda.split('\n').filter(Boolean),decisions:meetingForm.decisions.split('\n').filter(Boolean)};
      if(editingMeeting){
        await auditMeetingsApi.update(editingMeeting.id,payload);
      } else {
        await auditMeetingsApi.create(payload);
      }
      if(meetingDraft.saved(snapshot)) setMeetingFormOpen(false);
      if(meetingBuffer.active) await load();
    } catch(e){if(meetingBuffer.active) setMeetingSaveError(getApiErrorMessage(e));}
    finally{meetingSaveRef.current=false;setMeetingSaving(false);}
  };

  const handleEditMeeting = async(m)=>{
    if(meetingSaveRef.current) return;
    if(await meetingDraft.begin(m.id,meetingToForm(m))){setMeetingSaveError('');setMeetingFormOpen(true);}
  };
  const handleNewMeeting=async()=>{
    if(meetingSaveRef.current) return;
    if(await meetingDraft.begin(null,EMPTY_MEETING_FORM)){setMeetingSaveError('');setMeetingFormOpen(true);}
  };

  const handleMeetingPdf = async(meetingId)=>{
    try{const r=await auditMeetingsApi.pdf(meetingId);savePdfBlob(r, `meeting_${meetingId}.pdf`);}
    catch(e){alert(getApiErrorMessage(e));}
  };

  const handleGenerateDocuments = async (documentType = 'all', overwrite = false, options = {}) => {
    try {
      setGenerationState({ busy:true, type:documentType, result:null });
      const res = await workspaceApi.generateDocuments(id, { document_type: documentType, overwrite });
      setGenerationState({ busy:false, type:null, result:res.data });
      await load();
      const programId = (
        (res.data?.results || []).find((item) => item?.type === 'program' && item?.id)?.id
        || res.data?.bundle?.program?.id
        || null
      );
      if (options.openProgram && (documentType === 'program' || programId)) {
        navigate(`/audit-programs?audit_id=${id}${programId ? `&program_id=${programId}` : ''}`);
        return;
      }
      openTab('documents', { scroll: true });
      const changedCount = (res.data?.results || []).filter((item) => item?.created || item?.updated).length;
      showToast({
        type: 'success',
        message: changedCount ? uiText("Комплект документов сформирован") : uiText("Комплект документов уже актуален"),
        detail: (res.data?.results || []).map(formatGenerationItem).join(' · ') || undefined,
      });
    } catch (e) {
      setGenerationState({ busy:false, type:null, result:null });
      alert(getApiErrorMessage(e));
    }
  };

  const handleCreateOrder = async () => {
    const signatory = audit?.meta?.signatory || null;
    const signerName = signatory?.full_name || audit?.meta?.signature_name || user?.full_name || user?.username || '';
    if (!signerName.trim()) {
      alert(uiText("Укажите представителя с правом подписи приказа в карточке аудита. Права настраиваются в разделе «Компании» → «Сотрудники»."));
      return;
    }
    if (!await confirm(uiText("Оформить приказ на проведение аудита от имени: {v0}?", {v0: (signerName)}), { title: uiText("Оформление приказа"), confirmLabel: uiText("Оформить"), danger: false })) return;
    setOrderBusy(true);
    try {
      await documentSignaturesApi.create({
        audit_id: Number(id),
        document_kind: 'order',
        role: 'signatory',
        signatory_id: signatory?.id || null,
        full_name_text: signerName,
        position_text: signatory?.position || '',
        method: 'paper',
        notes: 'Приказ на проведение аудита оформлен в системе',
      });
      await load();
      openTab('documents', { scroll: true });
    } catch (e) {
      alert(getApiErrorMessage(e) || uiText("Не удалось оформить приказ"));
    } finally {
      setOrderBusy(false);
    }
  };

  const handleRequestEditAccess = async () => {
    try {
      setAccessRequestBusy(true);
      const note = await prompt(uiText("Коротко опишите, зачем вам нужен доступ к редактированию этого аудита."), {
        title: uiText("Запрос доступа к редактированию"),
        placeholder: uiText("Цель запроса"),
        confirmLabel: uiText("Отправить запрос"),
      });
      if (note === null) return;
      await auditsApi.requestEditAccess(id, { note });
      await load();
      alert(uiText("Запрос отправлен владельцу аудита."));
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setAccessRequestBusy(false);
    }
  };

  const handleApproveEditAccess = async (requestId) => {
    try {
      setAccessRequestBusy(true);
      await auditsApi.approveEditAccess(id, requestId, {});
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setAccessRequestBusy(false);
    }
  };

  const handleRejectEditAccess = async (requestId) => {
    try {
      setAccessRequestBusy(true);
      const decision_note = await prompt(uiText("При необходимости добавьте комментарий к отказу."), {
        title: uiText("Отказ в доступе"),
        placeholder: uiText("Комментарий"),
        confirmLabel: uiText("Отклонить запрос"),
      });
      if (decision_note === null) return;
      await auditsApi.rejectEditAccess(id, requestId, { decision_note });
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setAccessRequestBusy(false);
    }
  };

  const handleControlModeChange = async (value) => {
    try {
      setProcedureBusy(true);
      const payload = {
        audit_id: Number(id),
        control_mode_snapshot: value,
        program_status: procedureControl?.program_status || 'draft',
        report_status: procedureControl?.report_status || 'draft',
        disagreement_status: procedureControl?.disagreement_status || 'none',
        corrective_status: procedureControl?.corrective_status || 'not_required',
        verification_status: procedureControl?.verification_status || (value === 'none' ? 'not_required' : 'pending'),
        closing_status: procedureControl?.closing_status || 'open',
        conflict_checked: !!procedureControl?.conflict_checked,
        confidentiality_ack: !!procedureControl?.confidentiality_ack,
        kickoff_done: !!procedureControl?.kickoff_done,
        closing_meeting_done: !!procedureControl?.closing_meeting_done,
        object_notified_at: procedureControl?.object_notified_at || null,
        evidence_verified_at: procedureControl?.evidence_verified_at || null,
        verified_by_name: procedureControl?.verified_by_name || null,
        disagreement_note: procedureControl?.disagreement_note || null,
        closing_note: procedureControl?.closing_note || null,
      };
      await procedureControlApi.upsert(payload);
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setProcedureBusy(false);
    }
  };

  const handleEscalateNow = async () => {
    openTab('corrective', { scroll: true });
  };

  const handleFormalClose = async () => {
    try {
      setProcedureBusy(true);
      await procedureWorkflowApi.saveByAudit(id, { mark_closed: true, closing_status: 'closed_by_aas', closing_basis: 'Формальное закрытие выполнено из карточки аудита' });
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setProcedureBusy(false);
    }
  };

  const handleDocumentPdf = async(kind, itemId)=>{
    try {
      if (!itemId) return;
      const map = {
        order: () => documentSignaturesApi.pdf(itemId),
        program: () => auditProgramsApi.pdf(itemId),
        act: () => auditReportsApi.pdf(itemId),
        report: () => auditReportsApi.pdf(itemId),
        prescription: () => correctivePlansApi.pdf(itemId),
        corrective: () => correctivePlansApi.pdf(itemId),
        remediation: () => correctivePlansApi.remediationPdf(itemId),
        registry: () => correctivePlansApi.pdf(itemId),
      };
      const loader = map[kind];
      if (!loader) return;
      const res = await loader();
      savePdfBlob(res, `${kind}_${itemId}.pdf`);
    } catch (e) {
      alert(getApiErrorMessage(e));
    }
  };

  const handleSaveCA = async()=>{
    if(caSaveRef.current || caBuffer.recovery)return;
    if(!caForm.title.trim())return alert(uiText("Укажите название"));
    caSaveRef.current=true;
    setCASaveError('');
    const snapshot=caBuffer.snapshot();
    setCASaving(true);
    try{await correctiveActionsApi.create({...caForm,description:caForm.description,audit_id:Number(id)});
      caBuffer.acknowledge(snapshot,{committed:true});caBuffer.hydrate({form:emptyCA});if(!caBuffer.count)setCAFormOpen(false);await load();}
    catch(e){const message=getApiErrorMessage(e);setCASaveError(message);alert(message);}
    finally{caSaveRef.current=false;setCASaving(false);}
  };

  const handleCreateExtraQ = async()=>{
    if(!extraForm.text.trim())return alert(uiText("Введите текст"));
    try{await auditsApi.createExtraQuestion(id,{section_title:extraForm.section_title||'Дополнительные вопросы',text:extraForm.text.trim(),question_type:extraForm.question_type,options:extraForm.question_type==='select'?extraForm.options_raw.split(/\n|,/).map(v=>v.trim()).filter(Boolean):null,required:!!extraForm.required,answer_scope:extraForm.answer_scope,allow_comment:!!extraForm.allow_comment,allow_photo:!!extraForm.allow_photo});
      setExtraForm({section_title:'Дополнительные вопросы',text:'',question_type:'text',options_raw:'',required:false,answer_scope:'auditor',allow_comment:false,allow_photo:false});
      setShowExtraForm(false);await load();}
    catch(e){alert(getApiErrorMessage(e));}
  };

  const handleAssignmentResponse = async(action)=>{
    if(!myAssignment?.id) return;
    const note = action === 'decline' ? await prompt(uiText("При необходимости укажите причину отказа от назначения."), {
      title: uiText("Отказ от назначения"),
      placeholder: uiText("Причина отказа"),
      confirmLabel: uiText("Отказаться"),
    }) : '';
    if (action === 'decline' && note === null) return;
    try {
      setAssignmentBusy(true);
      await auditAssignmentsApi.respond(myAssignment.id, { action, note: note.trim() || null });
      await load();
    } catch (e) {
      alert(getApiErrorMessage(e));
    } finally {
      setAssignmentBusy(false);
    }
  };

  const latestEscalation = useMemo(() => {
    const list = Array.isArray(documentBundle.escalations) ? [...documentBundle.escalations] : [];
    list.sort((a, b) => new Date(b.event_date || b.created_at || 0) - new Date(a.event_date || a.created_at || 0));
    return list[0] || null;
  }, [documentBundle.escalations]);

  if(loading) return <div className="loading-center"><div className="spinner"/></div>;
  if(!audit) return (
    <div className="workspace-page">
      <PageIntro
        icon={Lock}
        eyebrow={uiText("Карточка аудита")}
        title={uiText("Аудит недоступен")}
        description={loadError || uiText("Аудит не найден либо у вашей учётной записи нет доступа к этой проверке.")}
        aside={<Link className="btn btn-primary" to="/audits"><ArrowLeft size={16} />{uiText(" К списку аудитов")}</Link>}
      />
    </div>
  );

  const s = STATUS_MAP[audit.status]||{label:audit.status || uiText("Статус не указан"),cls:'badge-gray'};
  const totalQ = allQuestions.length;
  const customerChecklistSections = (audit.template?.sections || [])
    .map((section) => ({
      ...section,
      questions: (section.questions || []).filter((question) => {
        const scope = question.effective_answer_scope || question.answer_scope || 'auditor';
        return scope === 'customer' || scope === 'both';
      }),
    }))
    .filter((section) => section.questions.length > 0);
  const customerChecklistCount = customerChecklistSections.reduce((count, section) => count + (section.questions?.length || 0), 0);
  // Проверяемая сторона видит контекст проверки целиком. Право редактирования
  // по-прежнему вычисляется отдельно для каждого вопроса в ChecklistSection.
  const visibleChecklistSections = audit.template?.sections || [];
  const pct = totalQ>0?Math.round(answeredQ/totalQ*100):0;
  const quickOpenTab = (tab) => openTab(tab, { scroll: true });
  const programEditorUrl = `/audit-programs?audit_id=${id}${documentBundle.program?.id ? `&program_id=${documentBundle.program.id}` : ''}`;
  const correctivePlanUrl = `/corrective-plans?audit_id=${id}`;
  const programDocumentId = documentBundle.program?.id;
  const openProgramPdf = programDocumentId ? () => handleDocumentPdf('program', programDocumentId) : null;
  const programCardUrl = isCustomer ? null : programEditorUrl;
  const orderPdf = procedureDocuments.order?.id ? () => handleDocumentPdf('order', procedureDocuments.order.id) : null;
  const docUrl = (entry, url) => (isCustomer ? (entry?.exists ? url : null) : url);
  const correctiveCardUrl = docUrl(procedureDocuments.corrective, correctivePlanUrl);
  const registryCardUrl = docUrl(procedureDocuments.registry, correctivePlanUrl);
  const actionTarget = (action) => {
    if (['build_program', 'agree_program'].includes(action?.key)) return programEditorUrl;
    if (['issue_corrective', 'wait_remediation', 'verify_evidence'].includes(action?.key)) return correctivePlanUrl;
    return action?.to || `/audits/${id}?tab=documents`;
  };
  const actionLabel = (action) => {
    if (['build_program', 'agree_program'].includes(action?.key)) return uiText("Открыть график плана");
    if (['issue_corrective', 'wait_remediation', 'verify_evidence'].includes(action?.key)) return uiText("Открыть ПКПД");
    return uiText("Открыть шаг");
  };
  const openProcedureAction = (action) => openActionTarget(actionTarget(action), 'documents');
  const controlModeValue = procedureControl?.control_mode_snapshot || procedureSummary?.control_mode?.value || audit?.meta?.control_mode || 'customer';
  const auditTypeValue = audit?.meta?.audit_type || audit?.template?.audit_type || '';
  const subjectKindValue = audit?.meta?.subject_kind || audit?.meta?.category || '';
  const auditTypeLabel = AUDIT_TYPE_LABELS[auditTypeValue] || auditTypeValue || '—';
  const subjectKindLabel = SUBJECT_KIND_LABELS[subjectKindValue] || subjectKindValue || '—';
  const verificationValue = procedureControl?.verification_status || (controlModeValue === 'none' ? 'not_required' : 'pending');
  const escalationValue = latestEscalation?.stage || (procedureSummary?.escalation_active ? 'first_warning' : 'none');
  const closureValue = documentBundle.closure?.closure_status || procedureControl?.closing_status || procedureWorkflow?.closing_status || 'open';
  const accessValue = canEdit ? 'granted' : 'view_only';
  const cockpitStatuses = [
    { key: 'stage', title: uiText("Стадия аудита"), meta: { label: procedureSummary?.current_stage_label || s.label, variant: 'info' }, hint: uiText("Показывает, на каком шаге процесса вы находитесь сейчас.") },
    { key: 'control', title: uiText("Режим контроля"), meta: pickMeta(CONTROL_MODE_META, controlModeValue, uiText("Не задан")), hint: pickMeta(CONTROL_MODE_META, controlModeValue, uiText("Не задан")).hint },
    { key: 'verification', title: uiText("Верификация"), meta: pickMeta(VERIFICATION_META, verificationValue, uiText("Не задана")), hint: uiText("Подтверждение, что замечания устранены и доказательства проверены.") },
    { key: 'escalation', title: uiText("Эскалация"), meta: pickMeta(ESCALATION_META, escalationValue, uiText("Нет")), hint: uiText("Включается, когда сроки сорваны или замечания не устранены.") },
    { key: 'closure', title: uiText("Формальное закрытие"), meta: pickMeta(CLOSURE_META, closureValue, uiText("Открыт")), hint: uiText("Последний шаг, после которого аудит считается завершённым формально.") },
    { key: 'access', title: uiText("Ваш доступ"), meta: pickMeta(ACCESS_STATUS_META, accessValue, uiText("Только просмотр")), hint: isCustomer ? uiText("Это аудит вашей компании. Редактирование доступно только на шагах, где от проверяемой стороны требуется действие.") : uiText("Любой аудитор может просматривать карточку. Изменять её могут назначенные аудиторы и администратор.") },
  ];
  const visibleCockpitStatuses = isCustomer
    ? cockpitStatuses.filter((item) => ['stage', 'access'].includes(item.key))
    : cockpitStatuses;
  const cockpitPrimaryActions = [
    canGenerateBundle ? { key: 'bundle', label: generationState.busy && generationState.type === 'all' ? uiText("Формируем комплект…") : uiText("Сформировать комплект"), onClick: () => handleGenerateDocuments('all', true), disabled: generationState.busy } : null,
    isCustomer && audit.status === 'program_sent' && documentBundle.program ? { key: 'agree-program', label: uiText("Согласовать план"), onClick: () => openTab('documents', { scroll: true }), disabled: false } : null,
    isCustomer && documentBundle.act ? { key: 'objection', label: uiText("Подать возражение"), onClick: () => openTab('documents', { scroll: true }), disabled: false } : null,
    canEdit ? { key: 'verify', label: uiText("Проверить выполненные КД"), onClick: () => navigate(`/corrective-plans?audit_id=${id}`), disabled: false } : null,
    canEdit ? { key: 'close', label: procedureBusy ? uiText("Закрываем…") : uiText("Закрыть аудит"), onClick: handleFormalClose, disabled: procedureBusy } : null,
  ].filter(Boolean);
  const auditorWaitsForProgramApproval = isAuditor && procedureActions.some((action) => action.key === 'agree_program');
  const primaryEditableProcedureAction = auditorWaitsForProgramApproval ? null : procedureActions[0];
  const focusItems = [
    openIssues > 0 ? { key: 'issues', title: uiText("Открытых замечаний: {v0}", {v0: (openIssues)}), text: uiText("Они попадут в итоговый отчёт и ПКПД. Закрытие аудита не блокируется автоматически, но решение будет видно в истории."), actionLabel: uiText("Перейти к замечаниям"), onClick: () => quickOpenTab('issues') } : null,
    !isCustomer && auditorWaitsForProgramApproval ? { key: 'program-wait', title: uiText("Ожидается согласование плана"), text: uiText("План направлен проверяемой стороне. Согласовать или отклонить его должен представитель организации; от аудитора сейчас действий не требуется.") } : null,
    !isCustomer && canEdit && primaryEditableProcedureAction ? { key: 'procedure', title: primaryEditableProcedureAction.title, text: primaryEditableProcedureAction.text, actionLabel: actionLabel(primaryEditableProcedureAction), onClick: () => openProcedureAction(primaryEditableProcedureAction) } : null,
    !isCustomer && missingKeys.size > 0 ? { key: 'checklist', title: uiText("Не заполнено обязательных пунктов: {v0}", {v0: (missingKeys.size)}), text: uiText("Проверьте обязательные вопросы чек-листа перед завершением полевого этапа."), actionLabel: uiText("Открыть чек-лист"), onClick: () => quickOpenTab('checklist') } : null,
  ].filter(Boolean).slice(0, 4);

  const nextSteps = [
    myAssignment?.status === 'pending' && user?.role === 'auditor' ? {
      key:'assignment',
      tone:'warning',
      title:uiText("Подтвердите участие в аудите"),
      description:uiText("Пока приглашение не принято, у вас не будет полного рабочего доступа внутри проверки."),
      actionLabel:uiText("Открыть приглашение"),
      onClick:() => document.getElementById('assignment-response-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
    } : null,
    !canEdit && isAuditor ? {
      key:'read-only',
      tone:'neutral',
      title:uiText("Аудит открыт только для просмотра"),
      description:uiText("Вы видите документы, ответы и историю проверки. Изменения доступны ответственному и назначенным аудиторам либо администратору."),
    } : null,
    audit.status === 'not_started' && canEdit ? {
      key:'start',
      tone:'info',
      title:uiText("Запустите аудит"),
      description:uiText("После старта будет проще вести чек-лист, протоколы и замечания по ходу проверки."),
      actionLabel:uiText("Начать аудит"),
      onClick:() => handleStatusChange('in_progress'),
    } : null,
    isCustomer && audit.status === 'program_sent' && documentBundle.program ? {
      key:'agree-program',
      tone:'warning',
      title:uiText("Согласуйте план аудита"),
      description:uiText("Проверьте область проверки, даты и состав группы. После согласования аудит перейдёт в работу."),
      actionLabel:uiText("Перейти к согласованию"),
      onClick:() => quickOpenTab('documents'),
    } : null,
    !isCustomer && missingKeys.size > 0 ? {
      key:'checklist',
      tone:'danger',
      title:uiText("Проверьте незаполненные пункты"),
      description:uiText("Не заполнено обязательных пунктов: {v0}.", {v0: (missingKeys.size)}),
      actionLabel:uiText("Открыть чек-лист"),
      onClick:() => quickOpenTab('checklist'),
    } : null,
    openIssues > 0 ? {
      key:'issues',
      tone:'warning',
      title:uiText("Разберите открытые замечания"),
      description:uiText("Сейчас открыто {v0} замеч. Проверьте статусы, комментарии и корректирующие действия.", {v0: (openIssues)}),
      actionLabel:uiText("Открыть замечания"),
      onClick:() => quickOpenTab('issues'),
    } : null,
    !isCustomer && meetings.length === 0 && canEdit ? {
      key:'meeting',
      tone:'neutral',
      title:uiText("Добавьте протокол совещания"),
      description:uiText("История совещаний помогает восстановить решения и договорённости по аудиту."),
      actionLabel:uiText("Открыть протоколы"),
      onClick:() => quickOpenTab('meetings'),
    } : null,
    !isCustomer && audit.status === 'ready' && canEdit ? {
      key:'finish',
      tone: vv?.canComplete ? 'success' : 'neutral',
      title: vv?.canComplete ? uiText("Аудит можно завершать") : uiText("Проверьте готовность к закрытию"),
      description: vv?.canComplete ? uiText("Можно переводить аудит в завершённый. Незакрытые подсказки останутся в контрольном списке.") : uiText("Перед закрытием посмотрите чек-лист, замечания и ПКПД, чтобы решение было осознанным."),
      actionLabel: vv?.canComplete ? uiText("Завершить аудит") : uiText("Открыть историю"),
      onClick:() => vv?.canComplete ? handleStatusChange('completed') : quickOpenTab('history'),
    } : null,
  ].filter(Boolean).slice(0, 3);

  const auditProcessIndex = (() => {
    if (['completed', 'closed'].includes(audit.status)) return 5;
    if (['ready', 'awaiting_closure'].includes(audit.status)) return 5;
    if (procedureDocuments.corrective?.exists || procedureDocuments.remediation?.exists) return 4;
    if (procedureDocuments.act?.exists || openIssues > 0) return 3;
    if (audit.status === 'in_progress') return 2;
    if (['program_sent', 'program_confirmed'].includes(audit.status) || procedureDocuments.program?.exists) return 1;
    return 0;
  })();
  const auditProcessSteps = [
    { key: 'created', label: uiText("Создан"), onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
    { key: 'program', label: uiText("План"), completed: Boolean(procedureDocuments.program?.exists || documentBundle.program), onClick: () => quickOpenTab('documents') },
    { key: 'execution', label: uiText("Проведение"), onClick: () => quickOpenTab('checklist') },
    { key: 'issues', label: uiText("Замечания"), onClick: () => quickOpenTab('issues') },
    { key: 'corrective', label: uiText("ПКПД"), onClick: () => openActionTarget(correctivePlanUrl, 'documents') },
    { key: 'closure', label: uiText("Закрытие аудита"), onClick: () => quickOpenTab('history') },
  ];
  const primaryNextStep = nextSteps[0] || focusItems[0] || null;
  const openBlocker = item => {
    if(item.registry) return navigate('/audits');
    if(item.plan) return navigate(`/corrective-plans?audit_id=${id}`);
    openTab(item.tab,{scroll:true});
    if(item.questionKey) requestAnimationFrame(()=>requestAnimationFrame(()=>checklistWorkbenchRef.current?.showQuestion(item.questionKey)));
  };

  return (
    <div className={isCustomer ? 'auditee-audit-detail' : 'ap-audit-workspace ap-studio-audit'}>
      {answerConflicts.length > 0 && <div className="ap-save-notice ap-save-notice--warning" role="alert"><div><strong>{uiText("Нужно сравнить изменённые ответы")}</strong><p>{uiText("Отправка приостановлена. Ваши изменения не удалены.")}</p><button type="button" className="ap-button ap-button--primary" onClick={()=>setConflictDialogOpen(true)}>{uiText("Сравнить варианты")}</button></div></div>}
      {conflictDialogOpen && answerConflicts[0] && <AnswerConflictDialog conflict={answerConflicts[0]} localAnswer={answerBuffer.values[answerConflicts[0].key]} question={allQuestions.find(question=>questionFrontendKey(question)===answerConflicts[0].key)?.text} onClose={()=>setConflictDialogOpen(false)} onResolve={choice=>{
        const conflict=answerConflicts[0];
        if(choice==='server') answerBuffer.acceptServer(conflict.key,{...conflict.server_answer,answer_revision:conflict.revision});
        else answerBuffer.edit(conflict.key,{...answerBuffer.values[conflict.key],expected_answer_revision:conflict.revision});
        setAnswerConflicts(items=>items.filter(item=>item.key!==conflict.key)); setSaveError('');
      }}/>} 
      {/* Header */}
      <div className="audit-detail-header">
        {isCustomer && <button type="button" className="btn btn-outline btn-sm audit-detail-back" onClick={()=>navigate('/audits')}>
          <ArrowLeft size={14}/> {isCustomer ? uiText("Мои аудиты") : uiText("Назад")}
        </button>}
        <div className="audit-detail-heading">
          <div className="audit-detail-title-row">
            <h1>{audit.title}</h1>
            <StatusBadge status={audit.status} type="audit" />
            {openIssues>0 && (
              <button type="button" className="badge badge-red" style={{cursor:'pointer',border:'none',display:'inline-flex',alignItems:'center',gap:4,color:'var(--premium-danger)'}}
                onClick={()=>{openTab('issues', { scroll: true });}}>
                <AlertTriangle size={11}/> {openIssues}{uiText(" откр. замеч.")}</button>
            )}
          </div>
          {audit.description && <p>{audit.description}</p>}
        </div>
        <div className="audit-detail-actions">
          {canEdit && audit.status==='not_started' && <button type="button" className="btn btn-primary btn-sm" disabled={Boolean(statusChanging)} onClick={()=>handleStatusChange('in_progress')}><Play size={14}/> {statusChanging==='in_progress'?uiText("Запускаем…"):uiText("Начать аудит")}</button>}
          {canEdit && audit.status==='in_progress' && <button type="button" className="btn btn-primary btn-sm audit-fieldwork-complete" disabled={Boolean(statusChanging)} onClick={()=>handleStatusChange('ready')}><CheckCircle size={14}/> {statusChanging==='ready'?uiText("Завершаем…"):uiText("Завершить полевой этап")}</button>}
          {canEdit && ['ready', 'awaiting_closure'].includes(audit.status) && <button type="button" className="btn btn-primary btn-sm" disabled={Boolean(statusChanging)} onClick={()=>handleStatusChange(audit.status==='awaiting_closure'?'closed':'completed')}><CheckCircle size={14}/> {['completed','closed'].includes(statusChanging)?uiText("Закрываем…"):uiText("Закрыть аудит")}</button>}
          {canEdit && audit.status!=='completed' && audit.status!=='suspended' && (
            <button type="button" className="btn btn-outline btn-sm" onClick={()=>{openTab('issues', { scroll: true });}}>
              <AlertTriangle size={14}/>{uiText(" Замечание")}</button>
          )}
          {!isCustomer && <details className="ap-audit-secondary-actions"><summary>{uiText("Экспорт и другие действия ")}<ChevronDown size={14} aria-hidden="true" /></summary><div>
          {!isCustomer ? <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowReportExport(true)}><Download size={14}/>{uiText(" Скачать отчёт")}</button> : null}
          {/* Кнопка «Применить санкции» (UC-9.3 FTT v1.1). Показывается admin/auditor.
              Открывает модалку с pre-filled audit_id и company_id. */}
          {canEdit && (
            <ApplySanctionsButton audit={audit} onApplied={() => load && load()} compact />
          )}
          {/* Suspend / Resume */}
          {canEdit && audit.status!=='completed' && audit.status!=='suspended' && (
            <button type="button" className="btn btn-outline btn-sm" onClick={()=>setShowSuspendModal(true)} title={uiText("Приостановить аудит")}>{uiText("⏸ Приостановить")}</button>
          )}
          </div></details>}
          {canEdit && audit.status==='suspended' && (
            <button type="button" className="btn btn-primary btn-sm" onClick={handleResume}>{uiText("▶ Возобновить")}</button>
          )}
        </div>
      </div>

      {!isCustomer && !audit.customer && ['aas', 'auditor', 'none'].includes(controlModeValue) ? (
        <div className="audit-internal-workflow-banner">
          <UserCheck size={18} />
          <div>
            <strong>{uiText("Аудит без представителя проверяемой организации")}</strong>
            <span>{uiText("Внешнее согласование и email-уведомления не ожидаются. Программу фиксирует аудитор; представителя можно назначить позже через карточку компании и редактирование аудита.")}</span>
          </div>
        </div>
      ) : null}

      {statusBlocker && !isCustomer ? (
        <div ref={statusFeedbackRef} className="audit-status-transition-feedback" role="alert">
          <div className="audit-status-transition-icon" aria-hidden="true"><AlertTriangle size={18}/></div>
          <div className="audit-status-transition-copy">
            <strong>{uiText("Переход на следующий этап пока недоступен")}</strong>
            <span>{statusBlocker.message || uiText("Проверьте обязательные пункты аудита и повторите действие.")}</span>
            <AuditBlockerList validation={statusBlocker.validation} targetStatus={statusBlocker.targetStatus} onOpen={openBlocker} previewCount={5}/>
            <div className="audit-status-transition-summary">
              {vv?.missingRequired?.length ? <span>{uiText("Незаполненных пунктов: ")}{vv.missingRequired.length}</span> : null}
              {vv?.missingRequiredPhotos?.length ? <span>{uiText("Нет обязательных вложений: ")}{vv.missingRequiredPhotos.length}</span> : null}
              {vv?.missingEvidence?.length ? <span>{uiText("Нет подтверждающих материалов")}</span> : null}
              {vv?.missingNegativeEvidence?.length ? <span>{uiText("Отрицательных ответов без обоснования: ")}{vv.missingNegativeEvidence.length}</span> : null}
              {vv?.missingAuditedCompany?.length ? <span>{uiText("Не выбрана проверяемая компания")}</span> : null}
              {vv?.openIssuesCount ? <span>{uiText("Открытых замечаний: ")}{vv.openIssuesCount}</span> : null}
            </div>
            <div className="audit-status-transition-actions">
              {(vv?.missingRequired?.length || vv?.missingRequiredPhotos?.length || vv?.missingEvidence?.length || vv?.missingNegativeEvidence?.length) ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={()=>openTab('checklist',{scroll:true})}>{uiText("Открыть незаполненные пункты")}</button>
              ) : null}
              {vv?.missingAuditedCompany?.length ? (
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>navigate('/audits')}>{uiText("Перейти в реестр для редактирования")}</button>
              ) : null}
              {vv?.openIssuesCount ? (
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>openTab('issues',{scroll:true})}>{uiText("Открыть замечания")}</button>
              ) : null}
            </div>
          </div>
          <button type="button" className="audit-status-transition-close" aria-label={uiText("Закрыть сообщение")} onClick={()=>setStatusBlocker(null)}><X size={16}/></button>
        </div>
      ) : null}

      {/* Плашка для приостановленного аудита */}
      {audit.status === 'suspended' ? (
        <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#92400e', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{uiText("Аудит приостановлен")}</div>
            {audit.suspended_reason && <div style={{ fontSize: 13 }}>{uiText("Причина: ")}{audit.suspended_reason}</div>}
            {audit.suspended_until && <div style={{ fontSize: 12, marginTop: 2 }}>{uiText("Плановая дата возобновления: ")}{audit.suspended_until}</div>}
          </div>
          {canEdit && (
            <button type="button" className="btn btn-primary btn-sm" onClick={handleResume}>{uiText("Возобновить")}</button>
          )}
        </div>
      ) : null}

      {/* Если этот аудит — повторный, показываем ссылку на родителя */}
      {audit.parent_audit_id ? (
        <div className="alert" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', marginBottom: 16, fontSize: 13 }}>{uiText("Это повторный аудит к ")}<Link to={`/audits/${audit.parent_audit_id}`} style={{ color: '#1e40af', fontWeight: 600 }}>{uiText("аудиту #")}{audit.parent_audit_id}</Link>{uiText(". История проверок этой компании может содержать «повторные» несоответствия.")}</div>
      ) : null}

      {isCustomer && audit.status === 'program_sent' && documentBundle.program ? (
        <div className="alert" style={{ background: '#fff7ed', border: '1px solid #fed7aa', color: '#92400e', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{uiText("План аудита ожидает согласования")}</div>
            <div style={{ fontSize: 13 }}>{uiText("Откройте документы, проверьте план и подтвердите его. После этого аудит перейдёт в работу.")}</div>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => openTab('documents', { scroll: true })}>{uiText("Перейти к согласованию")}</button>
        </div>
      ) : null}

      {!isCustomer && <section className="ap-studio-audit-compass" aria-label={uiText("Рабочий контекст аудита")}><div><span className="ap-studio-kicker">{uiText("Этап ")}{auditProcessIndex + 1}{uiText(" из ")}{auditProcessSteps.length} · {auditProcessSteps[auditProcessIndex]?.label}</span><strong>{primaryNextStep?.title || uiText("Обязательных действий сейчас нет")}</strong><span className="ap-studio-muted">{primaryNextStep?.description || primaryNextStep?.text || uiText("Продолжайте работу с вопросами, материалами и документами.")}</span></div><div className="ap-studio-compass-actions">{primaryNextStep?.onClick && <button type="button" className="ap-button ap-button--secondary" onClick={primaryNextStep.onClick}>{primaryNextStep.actionLabel || uiText("Открыть следующий шаг")}<ArrowRight size={16} aria-hidden="true" /></button>}<button type="button" className="ap-button ap-button--secondary" aria-expanded={overviewOpen || myAssignment?.status === 'pending'} aria-controls="audit-studio-overview" onClick={() => setOverviewOpen(v => !v)}>{overviewOpen ? uiText("Свернуть обзор") : uiText("Обзор и управление")}<ChevronDown size={15} aria-hidden="true" /></button></div></section>}
      <div id="audit-studio-overview" className="ap-studio-audit-overview" hidden={!isCustomer && !overviewOpen && myAssignment?.status !== 'pending'}>
      <div className="workflow-focus-card">
        <div className="workflow-focus-main">
          <div className="workflow-focus-eyebrow">{uiText("Фокус")}</div>
          <div className="workflow-focus-title">
            {primaryNextStep?.title || uiText("Обязательных действий сейчас нет")}
          </div>
          <div className="workflow-focus-meta">
            <StatusBadge status={audit.status} type="audit" />
            <span className="workspace-muted">{uiText("Ответственный: ")}{audit.auditor?.full_name || uiText("не назначен")}
            </span>
            <span className="workspace-muted">{uiText("Плановая дата: ")}{fmt(audit.scheduled_date)}
            </span>
          </div>
          {primaryNextStep?.description || primaryNextStep?.text ? (
            <div className="workspace-muted">{primaryNextStep.description || primaryNextStep.text}</div>
          ) : null}
        </div>
        {primaryNextStep?.onClick ? (
          <button type="button" className="btn btn-primary" onClick={primaryNextStep.onClick}>
            {primaryNextStep.actionLabel || uiText("Открыть следующий шаг")} <ArrowRight size={15} />
          </button>
        ) : null}
      </div>

      <ProcessStepper
        steps={auditProcessSteps}
        currentIndex={auditProcessIndex}
        ariaLabel={uiText("Этапы аудита")}
      />
      {!isCustomer && !auditLocked && <AuditReadiness validation={audit.validation} preparing={['draft','not_started','program_sent','program_confirmed'].includes(audit.status)} targetStatus={audit.status==='awaiting_closure'?'closed':audit.status==='ready'?'completed':'ready'} onOpen={openBlocker}/>}

      <Disclosure disabled={isCustomer} title={uiText("Информация и управление аудитом")} hint={uiText("Команда, документы, условия завершения и история")} open={myAssignment?.status === 'pending' ? true : undefined}>
      {isCustomer ? (
        <div className="auditee-audit-overview">
          <div>
            <span>{uiText("Ваш аудитор")}</span>
            <strong>{audit.auditor?.full_name || uiText("Будет назначен")}</strong>
          </div>
          <div>
            <span>{uiText("Плановая дата")}</span>
            <strong>{fmt(audit.scheduled_date)}</strong>
          </div>
          <div>
            <span>{uiText("Вопросы для вашей стороны")}</span>
            <strong>{customerChecklistCount || uiText("Нет")}</strong>
          </div>
          <div>
            <span>{uiText("Открытые замечания")}</span>
            <strong>{openIssues}</strong>
          </div>
        </div>
      ) : <Disclosure title={uiText("Состояние аудита и доступные действия")} hint={uiText("Контроль, верификация, индекс и документы")}><div className="audit-cockpit" style={{marginBottom:16}}>
        <div className="audit-cockpit-main">
          <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:16,flexWrap:'wrap'}}>
              <div>
                <div className="card-title">{uiText("Единое рабочее место аудита")}</div>
                <div style={{fontSize:13,color:'var(--premium-text-secondary)',marginTop:4}}>{uiText("Здесь видно, где вы находитесь в процессе, что важно прямо сейчас и какие действия доступны без перехода по разным разделам.")}</div>
              </div>
              <div className="workspace-chip-row">
                {visibleCockpitStatuses.slice(0, 3).map((item) => <Badge key={item.key} variant={item.meta.variant}>{item.title}: {item.meta.label}</Badge>)}
              </div>
            </div>
            <div className="card-body" style={{display:'grid',gap:14}}>
              <div className="audit-status-bar">
                {visibleCockpitStatuses.map((item) => (
                  <div key={item.key} className="audit-status-pill">
                    <div className="audit-status-pill-title">{item.title}</div>
                    <div className="audit-status-pill-row">
                      <Badge variant={item.meta.variant}>{item.meta.label}</Badge>
                    </div>
                    <div className="audit-status-pill-hint">{item.hint}</div>
                  </div>
                ))}
              </div>

              {complianceSummary ? (() => {
                // Цвет балла по диапазонам из обоих регламентов:
                //   ≥90 — отлично, ≥70 — удовл., ≥50 — неудовл., <50 — критическое.
                const ci = complianceSummary.compliance_index;
                const tone = ci >= 90 ? '#15803d' : ci >= 70 ? 'var(--premium-warning)' : ci >= 50 ? '#c2410c' : '#b91c1c';
                const bg = ci >= 90 ? '#f0fdf4' : ci >= 70 ? '#fefce8' : ci >= 50 ? '#fff7ed' : '#fef2f2';
                const border = ci >= 90 ? '#bbf7d0' : ci >= 70 ? '#fde68a' : ci >= 50 ? '#fed7aa' : '#fecaca';
                const breakdown = complianceSummary.breakdown || {};
                const findingTotal = Number(breakdown.critical || 0) + Number(breakdown.significant || 0) + Number(breakdown.minor || 0) + Number(breakdown.recommendation || 0);
                const statusForIndex = String(audit.status || '').toLowerCase();
                const hasAuditEvidence = findingTotal > 0 || answeredQ > 0 || ['ready', 'awaiting_closure', 'completed', 'closed'].includes(statusForIndex);
                const isPreliminaryIndex = !hasAuditEvidence;
                if (isPreliminaryIndex) {
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
                      background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                        <div style={{ fontSize: 11, color:'var(--premium-text-secondary)', textTransform: 'uppercase', letterSpacing: 0 }}>{uiText("Индекс аудита")}</div>
                        <div style={{ fontSize: 28, fontWeight: 800, color:'var(--premium-text-secondary)', lineHeight: 1 }}>—</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 600, color: '#344054' }}>{uiText("Будет рассчитан после заполнения чек-листа аудита")}</div>
                        <div style={{ fontSize: 12, color:'var(--premium-text-secondary)', marginTop: 2 }}>{uiText("Пока нет ответов или зафиксированных замечаний, система не показывает предварительное “100%”.")}</div>
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
                    background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 16px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                      <div style={{ fontSize: 11, color:'var(--premium-text-secondary)', textTransform: 'uppercase', letterSpacing: 0 }}>{isPreliminaryIndex ? uiText("Предварительный индекс") : uiText("Индекс соответствия")}</div>
                      <div style={{ fontSize: 36, fontWeight: 800, color: tone, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{ci}</div>
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 600, color: tone }}>{isPreliminaryIndex ? uiText("Оценка появится после первых ответов и наблюдений аудита") : complianceSummary.interpretation}</div>
                      <div style={{ fontSize: 12, color:'var(--premium-text-secondary)', marginTop: 2 }}>{complianceSummary.formula}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ textAlign: 'center', minWidth: 56 }}>
                        <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("КН")}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: breakdown.critical ? '#b91c1c' : 'var(--premium-text-secondary)' }}>{breakdown.critical || 0}</div>
                      </div>
                      <div style={{ textAlign: 'center', minWidth: 56 }}>
                        <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("ЗН")}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: breakdown.significant ? '#c2410c' : 'var(--premium-text-secondary)' }}>{breakdown.significant || 0}</div>
                      </div>
                      <div style={{ textAlign: 'center', minWidth: 56 }}>
                        <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("НН")}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: breakdown.minor ? 'var(--premium-warning)' : 'var(--premium-text-secondary)' }}>{breakdown.minor || 0}</div>
                      </div>
                      <div style={{ textAlign: 'center', minWidth: 64 }}>
                        <div style={{ fontSize: 11, color:'var(--premium-text-secondary)' }}>{uiText("Рек.")}</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color:'var(--premium-text-secondary)' }}>{breakdown.recommendation || 0}</div>
                      </div>
                    </div>
                  </div>
                );
              })() : null}

              {cockpitPrimaryActions.length ? <div>
                <div className="workspace-section-label" style={{marginBottom:8}}>{uiText("Быстрые действия")}</div>
                <div className="audit-action-bar">
                  {cockpitPrimaryActions.map((action) => (
                    <button key={action.key} type="button" className={`btn btn-sm ${action.key === 'close' ? 'btn-primary' : 'btn-outline'}`} disabled={action.disabled} onClick={action.onClick}>
                      {action.label}
                    </button>
                  ))}
                </div>
              </div> : null}
            </div>
          </div>
        </div>
        {(!isCustomer || focusItems.length > 0) ? <aside className="audit-cockpit-side">
          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Что важно сейчас")}</div>
              <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Без лишних переходов по вкладкам")}</div>
            </div>
            <div className="card-body" style={{display:'grid',gap:10}}>
              {focusItems.length ? focusItems.map((item) => (
                <div key={item.key} className="workspace-next-step">
                  <div className="workspace-item-title">{item.title}</div>
                  <div className="workspace-item-meta" style={{marginTop:6}}>{item.text}</div>
                  {item.onClick && <button type="button" className="btn btn-outline btn-sm" style={{marginTop:10}} onClick={item.onClick}>{item.actionLabel}</button>}
                </div>
              )) : <div className="workspace-empty">{uiText("Сейчас критичных блокеров нет. Можно продолжать работу по аудиту.")}</div>}
            </div>
          </div>
        </aside> : null}
      </div></Disclosure>}

      {/* Info grid */}
      {!isCustomer && <Disclosure title={uiText("Сведения и последние события")} hint={uiText("{v0} из {v1} вопросов заполнено", {v0: (answeredQ), v1: (totalQ)})}>
      {!isCustomer ? <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(155px,1fr))',gap:10,marginBottom:16}}>
        {[
          {label:uiText("Аудитор"),     value:audit.auditor?.full_name||'—',     icon:<Users size={11}/>},
          {label:uiText("Проверяемый"), value:audit.customer?.full_name||'—',    icon:<Eye size={11}/>},
          ...(!isCustomer ? [{label:uiText("Шаблон"), value:audit.template?.name||'—', icon:<BookOpen size={11}/>} ] : []),
          {label:uiText("Дата"),        value:fmt(audit.scheduled_date),          icon:<CalendarDays size={11}/>},
          ...(!isCustomer ? [{label:uiText("Прогресс"), value:`${answeredQ}/${totalQ} (${pct}%)`, icon:<ClipboardList size={11}/>, highlight:missingKeys.size>0}] : []),
          ...(meetings.length>0 ? [{label:uiText("Протоколов"),value:meetings.length,icon:<FileText size={11}/>}] : []),
          ...(correctiveActions.length>0 ? [{label:uiText("Мероприятия"),value:uiText("{v0} откр./{v1}", {v0: (correctiveActions.filter(c=>c.status==='open').length), v1: (correctiveActions.length)}),icon:<Wrench size={11}/>}] : []),
        ].map(m=>(
          <div className={`audit-detail-metric${m.highlight ? ' is-alert' : ''}`} key={m.label} style={{background:m.highlight?'#fff5f5':'white',border:`1px solid ${m.highlight?'#fca5a5':'#e5e7eb'}`,borderRadius:10,padding:'12px 14px'}}>
            <div style={{fontSize:11,color:m.highlight?'#dc2626':'var(--premium-text-secondary)',marginBottom:4,display:'flex',alignItems:'center',gap:4}}>{m.icon}{m.label}</div>
            <div style={{fontSize:14,fontWeight:600,color:m.highlight?'#dc2626':'inherit'}}>{m.value}</div>
            {m.highlight && <div style={{fontSize:11,color:'#dc2626',marginTop:2}}>{uiText("Обязательных пунктов без ответа: ")}{missingKeys.size}</div>}
          </div>
        ))}
      </div> : null}

      {!isCustomer && nextSteps.length ? (
        <div className="card" style={{marginBottom:16}}>
          <div className="card-header">
            <div className="card-title">{uiText("Следующие шаги по аудиту")}</div>
            <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Что стоит сделать после закрытия текущих блокеров")}</div>
          </div>
          <div className="card-body audit-next-steps-grid">
            {nextSteps.map((step) => (
              <div key={step.key} className="workspace-next-step">
                <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'flex-start'}}>
                  <div className="workspace-item-title">{step.title}</div>
                  <Badge variant={step.tone === 'danger' ? 'danger' : step.tone === 'warning' ? 'warning' : step.tone === 'success' ? 'success' : step.tone === 'info' ? 'info' : 'neutral'}>{step.tone === 'danger' ? uiText("Важно") : step.tone === 'warning' ? uiText("Скоро") : step.tone === 'success' ? uiText("Готово") : step.tone === 'info' ? uiText("Шаг") : uiText("Подсказка")}</Badge>
                </div>
                <div className="workspace-item-meta" style={{marginTop:8}}>{step.description}</div>
                {step.onClick && <button type="button" className="btn btn-outline btn-sm" style={{marginTop:12}} onClick={step.onClick}>{step.actionLabel} <ArrowRight size={14} /></button>}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!isCustomer && (audit.recent_activity || []).length ? (
        <div className="card" style={{marginBottom:16}}>
          <div className="card-header">
            <div className="card-title">{uiText("Последние действия по аудиту")}</div>
            <Badge variant="info">{(audit.recent_activity || []).length}</Badge>
          </div>
          <div className="card-body workspace-list">
            {(audit.recent_activity || []).slice(0, 6).map((item) => (
              <Link key={item.id} to={safeInternalRoute(item.to, `/audits/${id}?tab=history`)} className="workspace-list-item">
                <div>
                  <div className="workspace-item-title">{item.title}</div>
                  <div className="workspace-muted">{item.meta}{item.details ? ` · ${item.details}` : ''}</div>
                </div>
                <div className="workspace-date-pill">{fmt(item.date)} {fmtTime(item.date)}</div>
              </Link>
            ))}
          </div>
        </div>
      ) : null}

      </Disclosure>}

      {myAssignment && user?.role === 'auditor' && (
        <div id="assignment-response-card" className="card" style={{marginBottom:16, borderColor: myAssignment.status === 'pending' ? '#FCD34D' : myAssignment.status === 'accepted' ? '#86EFAC' : '#FCA5A5', background: myAssignment.status === 'pending' ? '#FFFBEB' : myAssignment.status === 'accepted' ? '#F0FDF4' : '#FEF2F2'}}>
          <div className="card-body" style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <div>
              <div style={{fontWeight:700,fontSize:14}}>{uiText("Ваше участие в аудите")}</div>
              <div style={{fontSize:13,color:'var(--premium-text-secondary)',marginTop:4}}>
                {myAssignment.status === 'pending' && uiText("Администратор пригласил вас в команду аудита. Подтвердите участие, чтобы получить право вести чек-лист и материалы.")}
                {myAssignment.status === 'accepted' && uiText("Вы подтверждены как участник команды аудита. Можно работать с чек-листом, замечаниями и протоколами.")}
                {myAssignment.status === 'declined' && uiText("Вы отказались от участия. При необходимости администратор может назначить вас повторно.")}
              </div>
              {myAssignment.note && <div style={{fontSize:12,color:'#444',marginTop:6}}>{uiText("Примечание администратора: ")}{myAssignment.note}</div>}
              {myAssignment.response_note && <div style={{fontSize:12,color:'#444',marginTop:6}}>{uiText("Ваш комментарий: ")}{myAssignment.response_note}</div>}
            </div>
            {myAssignment.status === 'pending' && (
              <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                <button type="button" className="btn btn-primary btn-sm" disabled={assignmentBusy} onClick={()=>handleAssignmentResponse('accept')}><CheckCircle size={14}/>{uiText(" Принять участие")}</button>
                <button type="button" className="btn btn-outline btn-sm" disabled={assignmentBusy} onClick={()=>handleAssignmentResponse('decline')}><X size={14}/>{uiText(" Отказаться")}</button>
              </div>
            )}
          </div>
        </div>
      )}

      {canManageTeam && (
        <Disclosure title={uiText("Команда аудита")} hint={uiText("Приняли участие: {v0} · Ожидают: {v1}", {v0: (teamAssignments.filter(a=>a.status==='accepted').length), v1: (teamAssignments.filter(a=>a.status==='pending').length)})}>
          <div className="card-header">
            <div className="card-title" style={{display:'flex',alignItems:'center',gap:8}}><UserCheck size={16}/>{uiText(" Администрирование аудита")}</div>
            <button type="button" className="btn btn-primary btn-sm" onClick={()=>setShowAssignModal(true)}>{uiText("Назначить / изменить состав")}</button>
          </div>
          <div className="card-body" style={{display:'grid',gap:12}}>
            <div style={{fontSize:13,color:'var(--premium-text-secondary)'}}>{uiText("Здесь администратор управляет составом команды. Назначение отправляет приглашение аудитору. Только подтвердившие аудиторы получают рабочие права внутри аудита.")}</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:10}}>
              <div style={{border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 14px',background:'#fff'}}>
                <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Ведущий аудитор")}</div>
                <div style={{fontWeight:700,marginTop:4}}>{audit.auditor?.full_name || uiText("Не определён")}</div>
                <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}>{uiText("Поле автоматически синхронизируется по принятым назначениям.")}</div>
              </div>
              <div style={{border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 14px',background:'#fff'}}>
                <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Состояние команды")}</div>
                <div style={{fontWeight:700,marginTop:4}}>{uiText("Приняли: ")}{teamAssignments.filter(a=>a.status==='accepted').length}{uiText(" · Ожидают: ")}{teamAssignments.filter(a=>a.status==='pending').length}</div>
                <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}>{uiText("Отказались: ")}{teamAssignments.filter(a=>a.status==='declined').length}</div>
              </div>
            </div>
          </div>
        </Disclosure>
      )}

      {/* Validation panel */}
      {!isCustomer && <Disclosure title={uiText("Маршрут аудита и организация")} hint={uiText("Регламент и сведения об организации")}>

      {procedureSummary && !isCustomer && (
        <div style={{display:'grid',gap:12,marginBottom:12}}>
          <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div className="card-title">{uiText("Маршрут аудита")}</div>
              <span className="badge badge-blue">{uiText("Текущий этап: ")}{procedureSummary.current_stage_label || '—'}</span>
            </div>
            <div className="card-body" style={{display:'grid',gap:12}}>
              <div style={{fontSize:13,color:'var(--premium-text-secondary)'}}>{uiText("Система показывает общий lifecycle аудита. Формальные документы появляются как профильные шаги, если выбранный вид аудита этого требует.")}</div>
              <div className="audit-route-grid" style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:10}}>
                {procedureTimeline.map((item, index)=> (
                  <div key={item.key} className={`audit-route-stage${item.done ? ' is-done' : ''}${item.key === procedureSummary.current_stage ? ' is-current' : ''}`}>
                    <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'flex-start'}}>
                      <div className="audit-route-stage-title"><span>{index + 1}</span>{item.label}</div>
                      <span className={`badge ${item.done?'badge-green':'badge-gray'}`}>{item.done?uiText("Готово"):uiText("Не готово")}</span>
                    </div>
                    {item.note && <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:6}}>{item.note}</div>}
                    {item.date && <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:6}}>{fmt(item.date)} {fmtTime(item.date)}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:12}}>
            <div className="card">
              <div className="card-header"><div className="card-title">{uiText("Рекомендованный следующий шаг")}</div></div>
              <div className="card-body" style={{display:'grid',gap:10}}>
                {procedureActions.length===0 ? (
                  <div style={{fontSize:13,color:'var(--premium-text-secondary)'}}>{uiText("Критичных шагов по регламенту сейчас нет. Можно переходить к текущей работе по чек-листу и замечаниям.")}</div>
                ) : procedureActions.map((action)=>{
                  const waitingForAuditee = isAuditor && action.key === 'agree_program';
                  const ActionTag = waitingForAuditee ? 'div' : 'button';
                  return <ActionTag key={action.key} type={waitingForAuditee ? undefined : 'button'} onClick={waitingForAuditee ? undefined : () => openProcedureAction(action)} className={`workspace-action-tile${waitingForAuditee ? ' is-waiting' : ''}`} style={{textDecoration:'none',color:'inherit',border:'1px solid #E4E7EC',borderRadius:14,padding:'12px 14px',display:'grid',gap:6,textAlign:'left',background:'#fff'}}>
                    <div style={{display:'flex',justifyContent:'space-between',gap:8,alignItems:'center'}}>
                      <div style={{fontWeight:700,fontSize:13}}>{waitingForAuditee ? uiText("Ожидается согласование плана") : action.title}</div>
                      <span className={`badge ${action.tone==='danger'?'badge-red':action.tone==='warning'?'badge-orange':action.tone==='success'?'badge-green':'badge-blue'}`}>{action.due_date ? fmt(action.due_date) : uiText("без срока")}</span>
                    </div>
                    <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{waitingForAuditee ? uiText("Согласование выполняет представитель проверяемой организации. От аудитора сейчас действий не требуется.") : action.text}</div>
                    {waitingForAuditee ? <div className="workspace-muted">{uiText("Система уведомит после решения представителя.")}</div> : <div style={{display:'inline-flex',alignItems:'center',gap:6,color:'var(--premium-accent-text)',fontWeight:700,fontSize:12}}>{uiText("Открыть ")}<ArrowRight size={13}/></div>}
                  </ActionTag>;
                })}
              </div>
            </div>

            <div className="card">
              <div className="card-header"><div className="card-title">{uiText("Официальные документы")}</div></div>
              <div className="card-body" style={{display:'grid',gap:10}}>
                {Object.entries({
                  order:uiText("Приказ"),
                  notice:uiText("Уведомление"),
                  program:uiText("План"),
                  act:uiText("Акт / отчёт"),
                  corrective:uiText("ПКПД"),
                  remediation:uiText("Отчёт о выполнении корректирующих действий"),
                  registry:uiText("Реестр устранения"),
                  closure:uiText("Закрытие"),
                }).map(([key,label])=>{
                  const doc = procedureDocuments[key] || {};
                  const docTarget = {
                    program: programEditorUrl,
                    corrective: correctivePlanUrl,
                    remediation: correctivePlanUrl,
                    registry: correctivePlanUrl,
                  }[key] || null;
                  const openDocumentCard = () => {
                    if (docTarget) navigate(docTarget);
                    else openTab('documents', { scroll: true });
                  };
                  return (
                    <button type="button" key={key} className="audit-official-document-card" onClick={openDocumentCard}>
                      <div>
                        <div style={{fontWeight:700,fontSize:13}}>{label}</div>
                        <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{doc.title || '—'}</div>
                      </div>
                      <div style={{display:'grid',justifyItems:'end',gap:4}}>
                        <span className={`badge ${doc.exists?'badge-green':'badge-gray'}`}>{doc.exists?uiText("Есть"):uiText("Нет")}</span>
                        <span style={{fontSize:11,color:'var(--premium-text-secondary)'}}>{doc.date ? fmt(doc.date) : '—'}</span>
                      </div>
                      <ArrowRight size={14} className="audit-official-document-arrow" aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Procedure details */}
      {!isCustomer ? <div className="card" style={{marginBottom:12}}>
        <div className="card-header"><div className="card-title"><Building2 size={15} style={{display:'inline',marginRight:6,verticalAlign:'middle'}}/>{uiText("Сведения по процедуре")}</div></div>
        <div className="card-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">{uiText("Вид аудита")}</label><div className="form-control" style={{background:'#fafafa'}}>{auditTypeLabel}</div></div>
            <div className="form-group"><label className="form-label">{uiText("Режим контроля")}</label><div className="form-control" style={{background:'#fafafa'}}>{pickMeta(CONTROL_MODE_META, controlModeValue, '—').label}</div></div>
            <div className="form-group"><label className="form-label">{uiText("Адрес площадки")}</label><div className="form-control" style={{background:'#fafafa'}}>{audit?.meta?.site_address||'—'}</div></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{uiText("Объект аудита")}</label><div className="form-control" style={{background:'#fafafa'}}>{audit?.meta?.audited_company?.name||'—'}{subjectKindLabel !== '—' ? ` · ${subjectKindLabel}` : ''}</div></div>
            <div className="form-group"><label className="form-label">{uiText("Заказчик")}</label><div className="form-control" style={{background:'#fafafa'}}>{audit?.meta?.customer_company?.name||audit?.meta?.external_customer_name||'—'}</div></div>
            <div className="form-group"><label className="form-label">{uiText("Подписант")}</label><div className="form-control" style={{background:'#fafafa'}}>{audit?.meta?.signature_name||'—'}</div></div>
          </div>
        </div>
      </div> : null}

      {/* R11 п.2: Self-check проверяемого виден аудитору при подготовке к аудиту.
          Помогает сравнить заявленное с фактическим в ходе проверки (FTT 4.4). */}
      {audit?.meta?.audited_company?.id && (user?.role === 'admin' || user?.role === 'auditor' || user?.role === 'analyst') && (
        <SelfCheckSummary companyId={audit.meta.audited_company.id} />
      )}

      </Disclosure>}

      </Disclosure>

      </div>
      {!canEdit ? (
        <div className="card" style={{marginBottom:12, border:'1px solid #dbeafe', background:'#f8fbff'}}>
          <div className="card-body" style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
            <div>
              <div style={{fontWeight:700, display:'flex', alignItems:'center', gap:8}}><Lock size={16}/> {isCustomer ? uiText("Работа со стороны организации") : uiText("Режим просмотра")}</div>
              <div style={{fontSize:13, color:'var(--premium-text-secondary)', marginTop:4}}>
                {isCustomer
                  ? uiText("Вы просматриваете аудит своей компании. Заполнение доступно только для вопросов, адресованных проверяемой стороне, а согласование плана находится во вкладке «Документы».")
                  : uiText("Этот аудит доступен вам для просмотра. Изменять его могут только назначенные аудиторы и администратор.")}
              </div>
            </div>
            <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
              {isCustomer && audit.status === 'program_sent' ? <button type="button" className="btn btn-primary btn-sm" onClick={() => openTab('documents', { scroll: true })}>{uiText("Согласовать план")}</button> : null}
            </div>
          </div>
        </div>
      ) : null}

      <DraftRecoveryNotice buffer={answerBuffer} label={uiText("ответы чек-листа")} disabled={saving || !!statusChanging} />
      {(isAdmin || isAuditor) && <DraftRecoveryNotice buffer={expectationBuffer} label={uiText("ожидаемые ответы")} disabled={expectedAnswerGenerating} />}
      {saveError && <div className="ap-save-notice ap-save-notice--warning" role="alert"><AlertTriangle size={19} /><div><strong>{uiText("Сохранение не подтверждено")}</strong><p>{saveError}</p><p>{uiText("Не закрывайте вкладку до успешного сохранения. Проверьте соединение и повторите действие.")}</p></div></div>}
      {/* Draft bar */}
      {canAnswerChecklist && !auditLocked ? <div className="card" style={{marginBottom:12}}>
        <div className="ap-save-bar">
          <div role="status" aria-live="polite">
            <strong>{uiText("Черновик ответов")}</strong>
            <SaveStatus saving={saving || draftState.saving} error={saveError} recovery={answerBuffer.recovery} dirty={answerBuffer.count>0} draftSynced={!answerBuffer.needsSync} savedAt={draftState.savedAt}/>
            <div className="ap-save-bar__detail">
              {draftState.saving?uiText("Сохраняем черновик на сервер…"):answerBuffer.needsSync?uiText("Изменения ещё не отправлены на сервер"):answerBuffer.count?uiText("Черновик на сервере · нажмите «Сохранить ответы» для подтверждения"):draftState.savedAt?uiText("Ответы сохранены {v0} {v1}", {v0: (fmt(draftState.savedAt)), v1: (fmtTime(draftState.savedAt))}):uiText("Изменений для отправки нет")}
            </div>
            {(answerBuffer.recovery || statusChanging) && <p className="ap-readiness-note">{answerBuffer.recovery ? uiText("Сначала выберите, восстановить ли найденный черновик. До этого сохранение недоступно.") : uiText("Дождитесь завершения смены этапа — затем сохранение снова будет доступно.")}</p>}
          </div>
          <div className="ap-inline-actions">
            {answerBuffer.needsSync && <button type="button" className="ap-button ap-button--secondary ap-button--sm" disabled={draftState.saving || saving || !!answerBuffer.recovery} onClick={() => setRetryRevision(value => value + 1)}>{uiText("Повторить автосохранение")}</button>}
            <button type="button" className="ap-button ap-button--primary" disabled={saving || !!statusChanging || !!answerBuffer.recovery} onClick={handleSave}><Save size={14} /> {saving?uiText("Сохранение…"):uiText("Сохранить ответы")}</button>
          </div>
        </div>
      </div> : null}

      <ChecklistSubmission key={`${user?.id}:${id}`} auditId={id} external={isCustomer} canEdit={canAnswerChecklist && !auditLocked && !saving && !statusChanging && !answerBuffer.recovery}
        dirty={answerBuffer.count > 0} closed={auditLocked}
        pendingEvidence={checklistUploadCount > 0 || deletingFileId != null}
        refreshKey={`${draftState.savedAt || ''}:${(audit.files || []).map(f => f.id).sort((a, b) => a - b).join(',')}:${checklistQuestions.map(q => q.frontend_key || q.id).join(',')}`}
        onSave={async () => { await commitAnswers(); await load(); }} />

      {/* TABS */}
      {process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' && <TechnicalRecords refresh={classifierRefresh} auditId={id} locked={auditLocked} pending={answerBuffer.count > 0 || saving || statusChanging || checklistUploadCount > 0 || deletingFileId != null} onData={setTechnicalData} focusRequest={technicalFocus} onChanged={async () => { setClassifierRefresh(v => v + 1); await load(); }} />}
      {process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' && (isAdmin || isAuditor) && <AuditClassifier refresh={classifierRefresh} auditId={id} canEdit={canEdit} locked={auditLocked} pending={answerBuffer.count > 0 || saving || statusChanging || checklistUploadCount > 0 || deletingFileId != null} onChanged={async () => { setClassifierRefresh(v => v + 1); await load(); }} />}
      <div ref={tabsRef} className="audit-workspace-tabs" role="tablist" aria-label={uiText("Рабочие разделы аудита")}>
        <span className="audit-workspace-tabs-label"><LayoutGrid size={15} aria-hidden="true" /><span>{uiText("Рабочие разделы")}</span></span>
        {(() => {
          // Бага 17: проверяемый не формирует документы и не управляет совещаниями
          // и корр. действиями. Документы он только просматривает / согласует.
          // Поэтому ему скрываем tabs формирования (meetings, corrective, files, admin),
          // оставляя checklist (его ответы), issues (видит и обсуждает),
          // documents (read-only — кнопки «Сформировать» уже скрыты через canGenerateBundle),
          // comments и history.
          const allTabs = [
            ['checklist',  uiText("Чек-лист ({v0})", {v0: (totalQ)})],
            ['issues',     uiText("Замечания ({v0}){v1}", {v0: (audit.issues?.length||0), v1: (openIssues>0?uiText(' · {count} открыто', { count: openIssues }):'')})],
            ['meetings',   uiText("Протоколы ({v0})", {v0: (meetings.length)})],
            ['corrective', uiText("Корр. действия ({v0})", {v0: (correctiveActions.length)})],
            ['comments',   uiText("Комментарии ({v0})", {v0: (audit.comments?.length||0)})],
            ['files',      uiText("Вложения ({v0})", {v0: (audit.files?.length||0)})],
            ['documents',  uiText("Документы ({v0})", {v0: (visibleDocumentCount)})],
            ...(canManageTeam ? [['admin', uiText("Администрирование ({v0})", {v0: (teamAssignments.length)})]] : []),
            ['history',    uiText("История")],
          ];
          const customerVisible = new Set(['checklist','issues','corrective','files','documents','comments']);
          const customerLabels = {
            issues: uiText("Замечания ({v0})", {v0: (audit.issues?.length || 0)}),
            corrective: uiText("План действий ({v0})", {v0: (correctiveActions.length)}),
            comments: uiText("Обсуждение ({v0})", {v0: (audit.comments?.length || 0)}),
            files: uiText("Файлы ({v0})", {v0: (audit.files?.length || 0)}),
            documents: uiText("Документы ({v0})", {v0: (visibleDocumentCount)}),
          };
          const visible = isCustomer
            ? allTabs.filter(([k]) => customerVisible.has(k)).map(([k, label]) => [k, customerLabels[k] || label])
            : allTabs;
          return visible.map(([val,label])=>(
            <button
              key={val}
              className={`tab-btn${activeTab===val?' active':''}`}
              type="button"
              role="tab"
              aria-selected={activeTab === val}
              tabIndex={activeTab === val ? 0 : -1}
              onClick={()=>openTab(val)}
            >
              {label}
            </button>
          ));
        })()}
      </div>

      {/* в•ђв•ђв•ђ CHECKLIST в•ђв•ђв•ђ */}
      {activeTab==='checklist' && (
        <div ref={checklistRef}>
          {isCustomer ? (
            <div className="auditee-checklist-access-note" role="note">
              <span className="auditee-checklist-access-icon"><BookOpen size={18} /></span>
              <div>
                <strong>{uiText("Весь чек-лист доступен для просмотра")}</strong>
                <p>{audit.meta?.workflow_version === 'compliance-v2' ? uiText('Заполните доступные ответы, добавьте свидетельства и нажмите «Сохранить и отправить». Аудитор проверит полученные сведения. Доступно вопросов: ') : uiText("Заполнять можно только вопросы с пометкой «Для аудируемого» или «Оба». Для вашей стороны сейчас доступно: ")}{customerChecklistCount}{uiText(" из ")}{totalQ}.
                </p>
              </div>
            </div>
          ) : null}
          {canEdit && !auditLocked && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div className="card-title">{uiText("Дополнительные вопросы по месту")}</div>
                <button type="button" className="btn btn-outline btn-sm" onClick={()=>setShowExtraForm(v=>!v)}><PlusCircle size={14}/> {showExtraForm?uiText("Скрыть"):uiText("Добавить")}</button>
              </div>
              {showExtraForm && (
                <div className="card-body" style={{display:'grid',gap:10}}>
                  <input className="form-control" placeholder={uiText("Раздел")} value={extraForm.section_title} onChange={e=>setExtraForm(p=>({...p,section_title:e.target.value}))}/>
                  <textarea className="form-control" rows={2} placeholder={uiText("Текст вопроса")} value={extraForm.text} onChange={e=>setExtraForm(p=>({...p,text:e.target.value}))}/>
                  <div className="form-row">
                    <div className="form-group"><label className="form-label">{uiText("Тип")}</label><select className="form-control" value={extraForm.question_type} onChange={e=>setExtraForm(p=>({...p,question_type:e.target.value}))}>{EXTRA_Q_TYPES.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                    {audit.meta?.workflow_version !== 'compliance-v2' && <div className="form-group"><label className="form-label">{uiText("Кто отвечает")}</label><select className="form-control" value={extraForm.answer_scope} onChange={e=>setExtraForm(p=>({...p,answer_scope:e.target.value}))}><option value="auditor">{uiText("Аудитор")}</option><option value="customer">{uiText("Проверяемый")}</option><option value="both">{uiText("Оба")}</option></select></div>}
                  </div>
                  {extraForm.question_type==='select' && <textarea className="form-control" rows={3} placeholder={uiText("Варианты, каждый с новой строки")} value={extraForm.options_raw} onChange={e=>setExtraForm(p=>({...p,options_raw:e.target.value}))}/>}
                  <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                    <label style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}><input type="checkbox" checked={extraForm.required} onChange={e=>setExtraForm(p=>({...p,required:e.target.checked}))}/>{uiText(" Обязательный")}</label>
                    <label style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}><input type="checkbox" checked={!!extraForm.allow_comment} onChange={e=>setExtraForm(p=>({...p,allow_comment:e.target.checked}))}/>{uiText(" Текстовое пояснение")}</label>
                    <label style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}><input type="checkbox" checked={!!extraForm.allow_photo} onChange={e=>setExtraForm(p=>({...p,allow_photo:e.target.checked}))}/>{uiText(" Файл")}</label>
                  </div>
                  <div style={{display:'flex',justifyContent:'flex-end'}}><button type="button" className="btn btn-primary" onClick={handleCreateExtraQ}>{uiText("Сохранить вопрос")}</button></div>
                </div>
              )}
            </div>
          )}
          <ChecklistWorkbench key={`${user?.id}:${id}`} positionKey={`audit-pro:checklist-position:${user?.id}:${id}`} ref={checklistWorkbenchRef} sections={visibleChecklistSections} representative={isCustomer} answers={answers} expectedAnswers={(isAdmin || isAuditor) ? expectedAnswers : {}} files={audit.files || []}>{(section,navigation)=>(
            <ChecklistSection key={section.id} section={section} {...navigation} answers={answers} onAnswerChange={handleAnswerChange}
              canEdit={canParticipate&&!auditLocked&&!saving&&!statusChanging&&!issueSaving&&!answerBuffer.recovery} currentUserRole={user?.role}
              expectedAnswers={Object.fromEntries(Object.entries(expectedAnswers).map(([key, value]) => [key, { ...value, saving: !!expectedSaving[key] }]))}
              expectedAnswerDisabled={!!expectationBuffer.recovery || expectedAnswerGenerating}
              canManageExpectedAnswers={(isAdmin || isAuditor)}
              guidanceReadOnly={audit.meta?.workflow_version === 'compliance-v2'}
              renderTechnicalRecords={process.env.REACT_APP_ENABLE_CLASSIFIER === 'true' ? question => {
                const key = question.frontend_key || (question.extra_question_id ? `extra-${question.extra_question_id}` : `q-${question.question_id || question.id}`);
                const item = technicalData?.items?.find(i => i.frontend_key === key);
                return item ? <div className="classifier-actions" style={{ marginTop: 12 }}><button type="button" className="ap-button ap-button--secondary ap-button--sm" onClick={() => setTechnicalFocus({ key, nonce: Date.now() })}>Технические записи по вопросу ({technicalData.records.filter(r => r.item_key === item.key).length})</button></div> : null;
              } : null}
              selfAudit={audit.meta?.workflow_version === 'compliance-v2' && audit.meta?.audit_mode === 'self_audit'}
              renderDiscussion={audit.meta?.workflow_version === 'compliance-v2' && audit.meta?.audit_mode === 'self_audit'
                ? question => <QuestionDiscussion auditId={audit.id} question={question} comments={audit.comments || []} answers={audit.answers || []} disabled={!canParticipate || auditLocked} onSent={load} /> : null}
              onExpectedAnswerChange={changeExpectedAnswer}
              onExpectedAnswerSave={saveExpectedAnswer}
              onGenerateExpectedAnswer={(question) => setExpectedAnswerQuestion(question)}
              onFileUpload={handleQPhoto} auditFiles={audit.files||[]}
              onFileDelete={handleFileDelete}
              canDeleteFile={canDeleteAttachment}
              onRemoveQuestion={isAdmin ? setQuestionToRemove : null}
              removeQuestionDisabled={saving || !!statusChanging || issueSaving || expectedAnswerGenerating || Object.values(uploadProgress).some(progress => progress > 0 && progress < 100)}
              onCreateIssue={canEdit && !auditLocked ? openIssueForQuestion : null}
              onChangeQuestionScope={async(qId,scope)=>{try{await auditsApi.setQuestionScope(id,{question_id:qId,answer_scope:scope});await load();}catch(e){alert(getApiErrorMessage(e));}}}
              canChangeScope={canEdit&&!auditLocked&&audit.meta?.workflow_version !== 'compliance-v2'} uploadProgress={uploadProgress} missingRequiredKeys={missingKeys}/>
          )}</ChecklistWorkbench>
          {canAnswerChecklist && !auditLocked && (
            <div style={{marginTop:16,display:'flex',justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}><Save size={15} /> {saving?uiText("Сохранение..."):uiText("Сохранить ответы")}</button>
            </div>
          )}
          {isAdmin ? <RemovedAuditQuestions questions={audit.removed_questions || []} answers={audit.answers || []} files={audit.files || []} /> : null}
        </div>
      )}

      {/* ═══ ISSUES — видят все участники, включая Проверяемого ═══ */}
      {activeTab==='issues' && (
        <div ref={issuesRef} style={{display:'grid',gap:14}}>
          {isCustomer && (
            <div className="card" style={{background:'#fffbf0',border:'1px solid #fed7aa'}}>
              <div className="card-body" style={{fontSize:13,color:'#92400e'}}>
                <strong>{uiText("Замечания по аудиту")}</strong>{uiText(" — ниже все выявленные несоответствия. Вы можете оставлять сообщения по каждому пункту.")}</div>
            </div>
          )}
          {canEdit && !auditLocked && (
            <div className="card">
              <DraftRecoveryNotice buffer={issueBuffer} label={uiText("замечание")} disabled={issueSaving} />
              <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div className="card-title">{uiText("Добавить замечание")}</div>
                <span className="badge badge-orange">{uiText("Из аудита")}</span>
              </div>
              <fieldset className="card-body ap-form-lock" disabled={issueSaving || !!issueBuffer.recovery}>
                <input className="form-control" placeholder={uiText("Краткий заголовок")} value={issueForm.title} onChange={e=>setIssueForm(p=>({...p,title:e.target.value}))}/>
                <textarea className="form-control" rows={3} placeholder={uiText("Описание")} value={issueForm.description} onChange={e=>setIssueForm(p=>({...p,description:e.target.value}))}/>
                <input className="form-control" placeholder={uiText("Требование / ссылка на нарушенный пункт")} value={issueForm.requirement_ref} onChange={e=>setIssueForm(p=>({...p,requirement_ref:e.target.value}))}/>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">{uiText("Классификация")}</label><select className="form-control" value={issueForm.priority} onChange={e=>setIssueForm(p=>({...p,priority:e.target.value}))}>{(audit.meta?.workflow_version === 'compliance-v2' ? [{value:'major',label:uiText("Значительное (Major)")},{value:'minor',label:uiText("Незначительное (Minor)")},{value:'ofi',label:uiText("Возможность для улучшения (OFI)")},{value:'sp',label:uiText("Сильная сторона (SP)")}] : ISSUE_PRIORITY).map(o=><option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                  <div className="form-group"><label className="form-label">{uiText("Привязать к вопросу с несоответствием")}</label><select className="form-control" value={issueForm.question_id} onChange={e=>setIssueForm(p=>({...p,question_id:e.target.value}))}><option value="">{uiText("Без привязки")}</option>{negativeAnsweredQuestions.map(q=><option key={q.frontend_key||q.id} value={q.question_id||q.id}>{q.sectionTitle} — {q.text.slice(0,80)}</option>)}</select></div>
                </div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">{uiText("Директивный срок ПКПД")}</label><SafeDateInput value={issueForm.due_date} onChange={value=>setIssueForm(p=>({...p,due_date:value}))} onValidityChange={setIssueDateValid}/></div>
                </div>
                <div style={{display:'flex',justifyContent:'flex-end'}}><button type="button" className="ap-button ap-button--primary" disabled={issueSaving} onClick={handleCreateIssue}>{issueSaving ? uiText("Сохранение…") : uiText("Добавить замечание")}</button></div>
              </fieldset>
            </div>
          )}
          {(audit.issues||[]).length===0 ? (
            <div className="empty-state"><p>{uiText("Замечаний пока нет")}</p></div>
          ) : (audit.issues||[]).map(issue=>(
            <div key={issue.id} className="card">
              <div className="card-body" style={{display:'grid',gap:10}}>
                <div style={{display:'flex',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:14}}>{issue.title}</div>
                    <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:2}}>{issue.description||uiText("Без описания")}</div>
                    {issue.requirement_ref ? <div style={{fontSize:12,color:'#4b5563',marginTop:4}}>{uiText("Требование / ссылка: ")}<strong>{issue.requirement_ref}</strong></div> : null}
                    <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}>{uiText("Создал: ")}<strong>{issue.creator?.full_name||'—'}</strong>{uiText(" · Директивный срок ПКПД: ")}<strong>{fmt(issue.due_date)}</strong>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'flex-start'}}>
                    <span className={`badge ${['significant','critical','high'].includes(issue.priority)?'badge-red':['minor','medium'].includes(issue.priority)?'badge-orange':'badge-gray'}`}>
                      {ISSUE_PRIORITY_LABELS[issue.priority] || uiText("Классификация не указана")}
                    </span>
                    <span className={`badge ${['escalated','overdue'].includes(issue.status) ? 'badge-red' : 'badge-blue'}`}>{ISSUE_STATUS_LABELS[issue.status] || uiText("Статус не указан")}</span>
                  </div>
                </div>
                {canEdit && (
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {ISSUE_STATUS.map(opt=>(
                      <button type="button" key={opt.value} className={`btn btn-sm ${issue.status===opt.value?'btn-primary':'btn-outline'}`} onClick={()=>handleIssueStatus(issue.id,opt.value)}>{opt.label}</button>
                    ))}
                  </div>
                )}
                <div style={{borderTop:'1px solid #eee',paddingTop:10}}>
                  <div style={{fontWeight:600,marginBottom:8,display:'flex',alignItems:'center',gap:6}}><MessageCircle size={14}/>{uiText(" Переписка")}</div>
                  {(issue.messages||[]).length===0 ? <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Сообщений пока нет")}</div> : (
                    <div style={{display:'grid',gap:8,marginBottom:10}}>
                      {issue.messages.map(msg=>(
                        <div key={msg.id} style={{border:'1px solid #f0f0f0',borderRadius:8,padding:'8px 12px',background:isAuditeeRole(msg.author)?'#f0fff4':'#fff'}}>
                          <div style={{fontSize:11,color:'var(--premium-text-secondary)',marginBottom:3}}>{msg.author?.full_name} · {isAuditeeRole(msg.author)?uiText("проверяемый"):msg.author?.role==='auditor'?uiText("аудитор"):uiText("администратор")} · {fmt(msg.created_at)}</div>
                          <div style={{fontSize:13}}>{msg.text}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {canParticipate && !auditLocked && (
                    <div style={{display:'flex',gap:8}}>
                      <textarea className="form-control" rows={2} placeholder={uiText("Написать сообщение…")} value={issueMessages[issue.id]||''} onChange={e=>setIssueMessages(p=>({...p,[issue.id]:e.target.value}))}/>
                      <button type="button" className="btn btn-primary" onClick={()=>handleIssueMsg(issue.id)}><Send size={14}/></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* в•ђв•ђв•ђ DOCUMENTS в•ђв•ђв•ђ */}
      {activeTab==='documents' && (
        <div ref={documentsRef} style={{display:'grid',gap:16}}>
          <div className="card audit-documents-card">
            <div className="card-header audit-documents-header">
              <div className="audit-documents-heading">
                <span className="audit-documents-heading-icon" aria-hidden="true"><FileText size={20} /></span>
                <div>
                  <div className="card-title">{uiText("Официальные документы аудита")}</div>
                  <div className="workspace-muted">{uiText("Формирование, актуальность и скачивание обязательного комплекта.")}</div>
                </div>
              </div>
              <div className="workspace-chip-row audit-documents-header-actions">
                <Badge variant="neutral">{uiText("Этап: ")}{procedureSummary?.current_stage_label || '—'}</Badge>
                {!isCustomer ? <Link className="btn btn-outline btn-sm" to="/corrective-actions">{uiText("Открыть реестр ПКПД")}</Link> : null}
                {canGenerateBundle ? <button type="button" className="btn btn-primary btn-sm" disabled={generationState.busy} onClick={()=>handleGenerateDocuments('all', false)}>{generationState.busy && generationState.type==='all' ? uiText("Формируем комплект…") : uiText("Сформировать комплект")}</button> : null}
              </div>
            </div>
            <div className="card-body audit-documents-body">
              {isCustomer ? (
                <div className="alert" style={{background:'#fffbf0',border:'1px solid #fed7aa',color:'#92400e'}}>{uiText("Документы аудита формирует аудитор. Вам они доступны для просмотра и согласования. Для скачивания PDF используйте соответствующую кнопку у документа.")}</div>
              ) : null}
              {generationState.result ? (
                <div className="workspace-empty-state" style={{alignItems:'stretch', textAlign:'left'}}>
                  <div className="workspace-empty-state-title">{uiText("Комплект документов обновлён из карточки аудита")}</div>
                  <div className="workspace-empty-state-text">{generationState.result.results?.map(formatGenerationItem).join(' · ') || uiText("Готово.")}</div>
                  {generationProgramId ? (
                    <div className="workspace-chip-row" style={{marginTop:10}}>
                      <Link className="btn btn-primary btn-sm" to={`/audit-programs?audit_id=${id}&program_id=${generationProgramId}`}>{uiText("Открыть и отредактировать график плана")}</Link>
                    </div>
                  ) : null}
                  {generationSummary ? (
                    <div className="workspace-chip-row" style={{marginTop:8}}>
                      <Badge variant="info">{uiText("ИС: ")}{generationSummary.compliance_index ?? '—'}</Badge>
                      <Badge variant="neutral">{uiText("КН: ")}{generationSummary.critical_count ?? 0}</Badge>
                      <Badge variant="neutral">{uiText("ЗН: ")}{generationSummary.significant_count ?? 0}</Badge>
                      <Badge variant="neutral">{uiText("НН: ")}{generationSummary.minor_count ?? 0}</Badge>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="workspace-action-grid audit-documents-grid">
                {!isCustomer ? (
                  <DocumentStatusCard label={uiText("Приказ на проведение аудита")} entry={procedureDocuments.order} to={null} onPdf={orderPdf} createLabel={uiText("Оформить приказ")} onGenerate={canGenerateBundle ? handleCreateOrder : null} generateLabel={procedureDocuments.order?.exists ? uiText("Переоформить приказ") : uiText("Оформить приказ")} generationBusy={orderBusy} generationHint={uiText("Фиксирует основание проведения аудита и подписанта со стороны организации-инициатора.")} />
                ) : null}
                {(!isCustomer || procedureDocuments.program?.exists || documentBundle.program) ? <DocumentStatusCard label={uiText("План аудита")} entry={procedureDocuments.program} to={programCardUrl} onOpen={isCustomer ? openProgramPdf : null} openLabel={uiText("Открыть план")} onPdf={!isCustomer ? openProgramPdf : null} createLabel={uiText("Подготовить график")} onGenerate={canGenerateBundle ? ()=>handleGenerateDocuments('program', true, { openProgram: true }) : null} generationBusy={generationState.busy && generationState.type==='program'} generationHint={uiText("Формируется из областей проверки, интервью и почасового графика аудита.")} /> : null}
                {(!isCustomer || procedureDocuments.act?.exists || documentBundle.act) ? <DocumentStatusCard label={uiText("Итоговый акт / отчёт")} entry={procedureDocuments.act} to={null} onPdf={documentBundle.act?.id ? ()=>handleDocumentPdf('act', documentBundle.act.id) : null} createLabel={uiText("Оформить")} onGenerate={canGenerateBundle ? ()=>handleGenerateDocuments('act', true) : null} generationBusy={generationState.busy && generationState.type==='act'} generationHint={uiText("Собирает замечания в пункты акта и пересчитывает индекс соответствия.")} /> : null}
                {(!isCustomer || procedureDocuments.corrective?.exists || documentBundle.prescription) ? <DocumentStatusCard label={uiText("ПКПД")} entry={procedureDocuments.corrective} to={correctiveCardUrl} onPdf={documentBundle.prescription?.id ? ()=>handleDocumentPdf('prescription', documentBundle.prescription.id) : null} createLabel={uiText("Открыть ПКПД")} onGenerate={canGenerateBundle ? ()=>handleGenerateDocuments('prescription', true) : null} generationBusy={generationState.busy && generationState.type==='prescription'} generationHint={uiText("Создаёт процесс ПКПД по значимым несоответствиям.")} /> : null}
                {(!isCustomer || procedureDocuments.remediation?.exists || documentBundle.remediation) ? <DocumentStatusCard label={uiText("Отчёт о выполнении корректирующих действий")} entry={procedureDocuments.remediation} to={null} onPdf={documentBundle.remediation?.id ? ()=>handleDocumentPdf('remediation', documentBundle.remediation.id) : null} generationHint={uiText("Формируется автоматически только из выполненных действий, подтверждающих файлов и решений аудитора; ПКПД не дублирует.")} /> : null}
                {(!isCustomer || procedureDocuments.registry?.exists || documentBundle.registry) ? <DocumentStatusCard label={uiText("Реестр устранения")} entry={procedureDocuments.registry} to={registryCardUrl} onPdf={documentBundle.registry?.id ? ()=>handleDocumentPdf('registry', documentBundle.registry.id) : null} createLabel={uiText("Открыть реестр")} onGenerate={canGenerateBundle ? ()=>handleGenerateDocuments('registry', true) : null} generationBusy={generationState.busy && generationState.type==='registry'} generationHint={uiText("Синхронизируется из ПКПД и верификации пунктов.")} /> : null}
              </div>
              {!isCustomer ? (
                <div className="workspace-link-grid audit-document-shortcuts">
                  <button type="button" className="workspace-link-card" onClick={()=>openTab('documents', { scroll:true })}>{uiText("Возражения по акту")}{documentCounts.objections ? ` · ${documentCounts.objections}` : ''}</button>
                  <button type="button" className="workspace-link-card" onClick={()=>openTab('corrective', { scroll:true })}>{uiText("Эскалации и контроль ПКПД")}</button>
                  <button type="button" className="workspace-link-card" onClick={()=>openTab('files', { scroll:true })}>{uiText("Подтверждения отправки документов")}</button>
                </div>
              ) : null}
            </div>
          </div>

          {!isCustomer ? <div className="card">
            <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <div className="card-title">{uiText("Контроль, верификация и закрытие")}</div>
              <div className="workspace-chip-row">
                <Badge variant={pickMeta(VERIFICATION_META, procedureControl?.verification_status, '—').variant}>{uiText("Верификация: ")}{pickMeta(VERIFICATION_META, procedureControl?.verification_status, '—').label}</Badge>
                <Badge variant={pickMeta(CLOSURE_META, documentBundle.closure?.closure_status || procedureWorkflow?.closing_status || 'open', uiText("Открыт")).variant}>{uiText("Закрытие: ")}{pickMeta(CLOSURE_META, documentBundle.closure?.closure_status || procedureWorkflow?.closing_status || 'open', uiText("Открыт")).label}</Badge>
              </div>
            </div>
            <div className="card-body" style={{display:'grid',gap:16}}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{uiText("Режим контроля")}</label>
                  <select className="form-control" value={procedureControl?.control_mode_snapshot || procedureSummary?.control_mode?.value || 'customer'} disabled={!canEdit || procedureBusy} onChange={(e)=>handleControlModeChange(e.target.value)}>
                    <option value="none">{uiText("Контроль не требуется")}</option>
                    <option value="aas">{uiText("Контроль ведёт ААС")}</option>
                    <option value="customer">{uiText("Контроль ведёт заказчик")}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Подтверждение верификации")}</label>
                  <div className="form-control" style={{background:'#fafafa'}}>{procedureControl?.verified_by_name || '—'}{procedureControl?.evidence_verified_at ? ` · ${fmt(procedureControl.evidence_verified_at)}` : ''}</div>
                </div>
              </div>
              <div className="workspace-chip-row">
                {canEdit ? <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate(`/corrective-plans?audit_id=${id}`)}>{uiText("Проверить выполненные КД")}</button> : null}
                {canEdit ? <button type="button" className="btn btn-outline btn-sm" disabled={procedureBusy} onClick={handleEscalateNow}>{uiText("Запустить эскалацию")}</button> : null}
                {canEdit ? <button type="button" className="btn btn-primary btn-sm" disabled={procedureBusy} onClick={handleFormalClose}>{uiText("Формально закрыть аудит")}</button> : null}
              </div>
              <div className="workspace-grid-two">
                <div className="workspace-next-step">
                  <div className="workspace-item-title">{uiText("Что значит режим контроля")}</div>
                  <div className="workspace-muted" style={{marginTop:6}}>{uiText("Без контроля — аудит закрывается после оформления документов; контроль ААС — проверку выполнения ведёт команда ААС; контроль заказчика — исполнение подтверждает заказчик.")}</div>
                </div>
                <div className="workspace-next-step">
                  <div className="workspace-item-title">{uiText("Текущая логика закрытия")}</div>
                  <div className="workspace-muted" style={{marginTop:6}}>{uiText("До формального закрытия нужно оформить документы, получить и проверить доказательства устранения, затем зафиксировать результат верификации.")}</div>
                </div>
              </div>
            </div>
          </div> : null}

          {canManageAccess && pendingAccessRequests.length > 0 ? (
            <div className="card">
              <div className="card-header"><div className="card-title">{uiText("Запросы на доступ к редактированию")}</div></div>
              <div className="card-body workspace-list">
                {pendingAccessRequests.map((item) => (
                  <div key={item.id} className="workspace-list-item" style={{alignItems:'flex-start'}}>
                    <div style={{flex:1}}>
                      <div className="workspace-item-title">{item.requester?.full_name || uiText("Пользователь #{v0}", {v0: (item.requester_user_id)})}</div>
                      <div className="workspace-muted">{item.request_note || uiText("Без комментария")} · {fmt(item.requested_at)}</div>
                    </div>
                    <div className="workspace-chip-row">
                      <button type="button" className="btn btn-primary btn-sm" disabled={accessRequestBusy} onClick={()=>handleApproveEditAccess(item.id)}>{uiText("Выдать доступ")}</button>
                      <button type="button" className="btn btn-outline btn-sm" disabled={accessRequestBusy} onClick={()=>handleRejectEditAccess(item.id)}>{uiText("Отклонить")}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {documentBundle.program && isCustomer ? (
            <CustomerProgramAcknowledgment
              audit={audit}
              program={documentBundle.program}
              user={user}
              onAcknowledged={load}
            />
          ) : null}

          {documentBundle.act && isCustomer ? (
            <CustomerActAcknowledgment
              audit={audit}
              act={documentBundle.act}
              user={user}
              onAcknowledged={load}
            />
          ) : null}

          {documentBundle.act ? (
            <div className="card">
              <div className="card-header" style={{display:'flex',justifyContent:'space-between',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                <div className="card-title">{uiText("Пункты акта и возражения")}</div>
                <div className="workspace-chip-row">
                  <Badge variant="info">{uiText("Акт: ")}{documentBundle.act.act_number || `#${documentBundle.act.id}`}</Badge>
                  <button type="button" className="btn btn-outline btn-sm" onClick={() => openTab('comments', { scroll:true })}>{uiText("Обсудить акт")}</button>
                </div>
              </div>
              <div className="card-body workspace-list">
                {actFindings.length === 0 ? (
                  <div className="workspace-empty-state">
                    <div className="workspace-empty-state-title">{uiText("В акте пока нет пунктов несоответствий")}</div>
                    <div className="workspace-empty-state-text">{uiText("Как только аудитор зафиксирует пункты в итоговом акте, здесь можно будет подать возражение на каждый пункт отдельно.")}</div>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => openTab('documents', { scroll:true })}>{uiText("Открыть документы аудита")}</button>
                  </div>
                ) : actFindings.map((finding, index) => {
                  const targetRef = `finding-${index + 1}`;
                  const objectionCount = objectionCountByTarget[targetRef] || 0;
                  const title = finding.description || uiText("Пункт {v0}", {v0: (index + 1)});
                  return (
                    <div key={targetRef} className="workspace-list-item" style={{alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div className="workspace-item-title">{uiText("Пункт ")}{index + 1}. {title}</div>
                        <div className="workspace-muted">{finding.classification || '—'}{finding.requirement_ref ? ` · ${finding.requirement_ref}` : ''}</div>
                        {finding.evidence ? <div className="workspace-muted" style={{marginTop:4}}>{uiText("Доказательства: ")}{finding.evidence}</div> : null}
                        {finding.root_cause ? <div className="workspace-muted" style={{marginTop:4}}>{uiText("Причина: ")}{finding.root_cause}</div> : null}
                      </div>
                      <div style={{display:'grid',gap:8,minWidth:220}}>
                        <Badge variant={finding.classification === 'КН' ? 'danger' : finding.classification === 'ЗН' ? 'warning' : 'neutral'}>{finding.classification || uiText("Пункт акта")}</Badge>
                        <div className="workspace-date-pill">{uiText("Возражений: ")}{objectionCount}</div>
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => openTab('comments', { scroll:true })}>{uiText("Обсудить пункт")}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {!isCustomer ? <div className="workspace-grid-two">
            <div className="card">
              <div className="card-header"><div className="card-title">{uiText("Что ещё не оформлено")}</div></div>
              <div className="card-body workspace-list">
                {procedureActions.length === 0 ? (
                  <div className="workspace-empty-state">
                    <div className="workspace-empty-state-title">{uiText("Критичных пробелов нет")}</div>
                    <div className="workspace-empty-state-text">{uiText("Основные обязательные шаги по документам уже зафиксированы. Можно переходить к контролю исполнения и верификации.")}</div>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => openTab('corrective', { scroll:true })}>{uiText("Открыть ПКПД")}</button>
                  </div>
                ) : procedureActions.map((item) => (
                  <button key={item.key} type="button" onClick={() => openProcedureAction(item)} className="workspace-list-item workspace-list-item-button">
                    <div>
                      <div className="workspace-item-title">{item.title}</div>
                      <div className="workspace-muted">{item.text}</div>
                    </div>
                    <div className="workspace-chip-row">
                      {item.due_date ? <div className="workspace-date-pill"><CalendarDays size={14} /> {fmt(item.due_date)}</div> : null}
                      <Badge variant={item.tone || 'info'}>{item.tone === 'danger' ? uiText("Критично") : item.tone === 'warning' ? uiText("Важно") : uiText("Далее")}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="card">
              <div className="card-header"><div className="card-title">{uiText("Возражения и реестр устранения")}</div></div>
              <div className="card-body workspace-list">
                {documentCounts.objections === 0 ? (
                  <div className="workspace-empty-state">
                    <div className="workspace-empty-state-title">{uiText("Возражений пока нет")}</div>
                    <div className="workspace-empty-state-text">{uiText("Когда партнёр подаст письменные возражения на акт или отдельные пункты, они появятся здесь.")}</div>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => openTab('comments', { scroll:true })}>{uiText("Открыть обсуждение")}</button>
                  </div>
                ) : documentBundle.objections.map((item) => (
                  <button key={item.id} type="button" onClick={() => openTab('comments', { scroll:true })} className="workspace-list-item workspace-list-item-button">
                    <div>
                      <div className="workspace-item-title">{item.objection_number || uiText("Возражение #{v0}", {v0: (item.id)})}</div>
                      <div className="workspace-muted">{item.author_name || uiText("Автор не указан")} · {item.status_label || item.status}</div>
                    </div>
                    <div className="workspace-date-pill"><CalendarDays size={14} /> {fmt(item.submitted_at || item.updated_at)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div> : null}
        </div>
      )}


      {/* в•ђв•ђв•ђ MEETINGS в•ђв•ђв•ђ */}
      {activeTab==='meetings' && (
        <div>
          {isCustomer && (
            <div className="card" style={{background:'#f0f9ff',border:'1px solid #bae6fd',marginBottom:14}}>
              <div className="card-body" style={{fontSize:13,color:'#075985'}}>
                <strong>{uiText("Протоколы совещаний")}</strong>{uiText(" — ознакомьтесь с материалами вводного, промежуточных и итогового совещания по данному аудиту.")}</div>
            </div>
          )}
          {canEdit && (
            <div style={{marginBottom:14,display:'flex',justifyContent:'flex-end',flexWrap:'wrap',gap:8}}>
              {meetingFormOpen ? (
                <button type="button" className="btn btn-outline btn-sm" disabled={meetingSaving} onClick={()=>setMeetingFormOpen(false)}>{uiText("Свернуть форму")}</button>
              ) : (
                <button type="button" className="btn btn-primary btn-sm" disabled={meetingSaving || !!meetingBuffer.recovery} onClick={()=>setMeetingFormOpen(true)}><PlusCircle size={14}/> {meetingId || meetingBuffer.count ? uiText("Продолжить протокол") : uiText("Новый протокол")}</button>
              )}
              {(meetingId || meetingBuffer.count) && <button type="button" className="btn btn-outline btn-sm" disabled={meetingSaving || !!meetingBuffer.recovery} onClick={handleNewMeeting}>{uiText("Создать другой протокол")}</button>}
            </div>
          )}
          {canEdit && <DraftRecoveryNotice buffer={meetingBuffer} label={meetingDraft.recoveryId ? uiText("протокол №{v0} этого аудита", {v0: (meetingDraft.recoveryId)}) : uiText("новый протокол совещания")} disabled={meetingSaving}/>}
          {canEdit && <SaveStatus saving={meetingSaving} error={meetingSaveError} recovery={meetingBuffer.recovery} dirty={meetingBuffer.count>0}/>}
          {canEdit && meetingFormOpen && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header"><div className="card-title">{editingMeeting?uiText("Редактирование протокола №{v0}", {v0: (editingMeeting.id)}):uiText("Новый протокол совещания")}</div></div>
              <div className="card-body"><fieldset className="ap-form-lock" disabled={meetingSaving || !!meetingBuffer.recovery} style={{display:'grid',gap:10}}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{uiText("Тип совещания")}</label>
                    <select className="form-control" value={meetingForm.meeting_type} onChange={e=>setMeetingForm(p=>({...p,meeting_type:e.target.value}))}>
                      <option value="opening">{uiText("Вводное совещание")}</option>
                      <option value="intermediate">{uiText("Промежуточное совещание")}</option>
                      <option value="closing">{uiText("Итоговое совещание")}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label required">{uiText("Дата")}</label>
                    <SafeDateInput required value={meetingForm.meeting_date} onChange={value=>setMeetingForm(p=>({...p,meeting_date:value}))}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{uiText("Место")}</label>
                    <input className="form-control" value={meetingForm.location} onChange={e=>setMeetingForm(p=>({...p,location:e.target.value}))}/>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">{uiText("Ответственный")}</label><input className="form-control" value={meetingForm.responsible_name} onChange={e=>setMeetingForm(p=>({...p,responsible_name:e.target.value}))}/></div>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">{uiText("Участники (каждый с новой строки)")}</label><textarea className="form-control" rows={4} value={meetingForm.participants} onChange={e=>setMeetingForm(p=>({...p,participants:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">{uiText("Повестка (каждый пункт с новой строки)")}</label><textarea className="form-control" rows={4} value={meetingForm.agenda} onChange={e=>setMeetingForm(p=>({...p,agenda:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">{uiText("Решения (каждый пункт с новой строки)")}</label><textarea className="form-control" rows={4} value={meetingForm.decisions} onChange={e=>setMeetingForm(p=>({...p,decisions:e.target.value}))}/></div>
                </div>
                <div className="form-group"><label className="form-label">{uiText("Примечание")}</label><textarea className="form-control" rows={2} value={meetingForm.note} onChange={e=>setMeetingForm(p=>({...p,note:e.target.value}))}/></div>
                <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                  <button type="button" className="btn btn-outline" onClick={()=>setMeetingFormOpen(false)}>{uiText("Свернуть форму")}</button>
                  <button type="button" className="btn btn-primary" disabled={meetingSaving} onClick={handleSaveMeeting}>{meetingSaving?uiText("Сохранение…"):uiText("Сохранить протокол")}</button>
                </div>
              </fieldset></div>
            </div>
          )}
          {meetings.length===0 ? (
            <div className="empty-state"><FileText size={32} color="#ddd"/><p style={{marginTop:8}}>{uiText("Протоколов совещаний пока нет")}</p></div>
          ) : meetings.map(m=><MeetingCard key={m.id} meeting={m} canEdit={canEdit} onEdit={handleEditMeeting} onPdf={handleMeetingPdf}/>)}
        </div>
      )}

      {/* в•ђв•ђв•ђ CORRECTIVE ACTIONS в•ђв•ђв•ђ */}
      {activeTab==='corrective' && (
        <div>
          {canManageCorrectivePlan && (
            <div style={{marginBottom:14,display:'flex',justifyContent:'flex-end'}}>
              <button type="button" className="btn btn-primary btn-sm" onClick={()=>setCAFormOpen(v=>!v)}><PlusCircle size={14}/>{uiText(" Добавить действие ПКПД")}</button>
            </div>
          )}
          {canManageCorrectivePlan && <DraftRecoveryNotice buffer={caBuffer} label={uiText("новое корректирующее действие")} disabled={caSaving}/>}
          {canManageCorrectivePlan && <SaveStatus saving={caSaving} error={caSaveError} recovery={caBuffer.recovery} dirty={caBuffer.count>0}/>}
          {canManageCorrectivePlan && caFormOpen && (
            <div className="card" style={{marginBottom:16}}>
              <div className="card-header"><div className="card-title">{uiText("План корректирующих действий")}</div></div>
              <div className="card-body" style={{display:'grid',gap:10}}>
                <input className="form-control" placeholder={uiText("Действие по устранению несоответствия")} value={caForm.title} onChange={e=>setCAForm(p=>({...p,title:e.target.value}))}/>
                <textarea className="form-control" rows={3} placeholder={uiText("Что будет сделано")} value={caForm.description} onChange={e=>setCAForm(p=>({...p,description:e.target.value}))}/>
                <div className="form-row">
                  <div className="form-group"><label className="form-label">{uiText("Ответственный вручную")}</label><input className="form-control" placeholder={uiText("ФИО / должность")} value={caForm.responsible_name} onChange={e=>setCAForm(p=>({...p,responsible_name:e.target.value}))}/></div>
                  <div className="form-group"><label className="form-label">{uiText("Срок")}</label><input type="date" className="form-control" value={caForm.due_date} onChange={e=>setCAForm(p=>({...p,due_date:e.target.value}))}/></div>
                </div>
                <div className="form-group"><label className="form-label">{uiText("Как будет подтверждено устранение")}</label><textarea className="form-control" rows={2} placeholder={uiText("Файл, фото, комментарий, запись в журнале и т.п.")} value={caForm.evidence_comment} onChange={e=>setCAForm(p=>({...p,evidence_comment:e.target.value}))}/></div>
                <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                  <button type="button" className="btn btn-outline" onClick={()=>setCAFormOpen(false)}>{uiText("Отмена")}</button>
                  <button type="button" className="btn btn-primary" disabled={caSaving || !!caBuffer.recovery} onClick={handleSaveCA}>{caSaving?uiText("Сохранение…"):uiText("Сохранить в ПКПД")}</button>
                </div>
              </div>
            </div>
          )}
          {correctiveActions.length===0 ? (
            <div className="empty-state"><Wrench size={32} color="#ddd"/><p style={{marginTop:8}}>{uiText("Корректирующих мероприятий нет")}</p></div>
          ) : correctiveActions.map(ca=>(
            <div key={ca.id} className="card" style={{marginBottom:10}}>
              <div className="card-body">
                <div style={{display:'flex',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontWeight:700}}>{ca.title}</div>
                    {ca.description && <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:2}}>{ca.description}</div>}
                    <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}>
                      {ca.assignee?.full_name&&<>{uiText("Ответственный: ")}<strong>{ca.assignee.full_name}</strong> · </>}{uiText("Срок: ")}<strong>{fmt(ca.due_date)}</strong>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    <span className={`badge ${ca.status==='open'?'badge-red':ca.status==='in_progress'?'badge-blue':ca.status==='resolved'?'badge-orange':'badge-green'}`}>
                      {CORRECTIVE_STATUS_LABELS[ca.status] || uiText("Статус не указан")}
                    </span>
                    {(canEdit ? CORRECTIVE_STATUS : CORRECTIVE_STATUS.filter(st=>st.value!=='closed')).map(st=>(
                      <button type="button" key={st.value} className={`btn btn-sm ${ca.status===st.value?'btn-primary':'btn-outline'}`}
                        disabled={!canManageCorrectivePlan}
                        onClick={async()=>{try{await correctiveActionsApi.update(ca.id,{status:st.value});await load();}catch(e){alert(getApiErrorMessage(e));}}}>
                        {isCustomer ? (CORRECTIVE_STATUS_AUDITEE_LABELS[st.value] || st.label) : st.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{borderTop:'1px solid #eee',marginTop:12,paddingTop:12,display:'grid',gap:8}}>
                  <div style={{fontWeight:600,fontSize:13}}>{uiText("Доказательства устранения")}</div>
                  {(audit.files||[]).filter(f=>Number(f.issue_id)===Number(ca.id)).length ? (
                    <div style={{display:'grid',gap:8}}>
                      {(audit.files||[]).filter(f=>Number(f.issue_id)===Number(ca.id)).map(file=><AttachmentItem key={file.id} file={file} onDelete={canDeleteAttachment(file)?handleFileDelete:null} deleting={deletingFileId===file.id}/>) }
                    </div>
                  ) : <div style={{fontSize:12,color:'var(--premium-text-secondary)'}}>{uiText("Файлы или комментарии по устранению пока не приложены.")}</div>}
                  {(canManageCorrectivePlan || isCustomer) && !auditLocked ? (
                    <label className="btn btn-outline btn-sm auditee-evidence-upload">
                      <Upload size={14} />{uiText(" Приложить подтверждение")}<input type="file" className="sr-only" onChange={e=>{const file=e.target.files?.[0];handleIssueEvidenceUpload(ca,file);e.target.value='';}} />
                    </label>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
          <div style={{marginTop:12,textAlign:'center'}}>
            <Link to={isCustomer ? `/corrective-plans?audit_id=${id}` : '/corrective-actions'} className="btn btn-outline btn-sm">
              <ExternalLink size={13}/> {isCustomer ? uiText("Открыть ПКПД, причины и доказательства") : uiText("Полный реестр корректирующих действий")}
            </Link>
          </div>
        </div>
      )}

      {/* в•ђв•ђв•ђ COMMENTS в•ђв•ђв•ђ */}
      {activeTab==='comments' && (
        <div>
          {(!audit.comments||!audit.comments.length) && <div className="empty-state" style={{padding:24}}><p>{uiText("Комментариев пока нет")}</p></div>}
          {(audit.comments||[]).map(c=>(
            <div key={c.id} className="comment-item">
              <div style={{display:'flex',justifyContent:'space-between'}}>
                <span className="comment-author">{c.author?.full_name}</span>
                <span className="comment-meta">{fmt(c.created_at)}</span>
              </div>
              <div className="comment-text">{c.text}</div>
            </div>
          ))}
          {canParticipate && <DraftRecoveryNotice buffer={commentBuffer} label={uiText("комментарий к аудиту")} disabled={commentSaving}/>}
          {canParticipate && <SaveStatus saving={commentSaving} error={commentSaveError} recovery={commentBuffer.recovery} dirty={commentBuffer.count>0}/>}
          {canParticipate && (
            <div style={{display:'flex',gap:8,marginTop:12}}>
              <textarea className="form-control" placeholder={uiText("Добавить комментарий…")} value={comment} onChange={e=>setComment(e.target.value)} rows={3}/>
              <button type="button" className="btn btn-primary" aria-label={uiText("Отправить комментарий")} title={uiText("Отправить комментарий")} onClick={handleComment} disabled={!comment.trim() || commentSaving || !!commentBuffer.recovery}><Send size={14}/></button>
            </div>
          )}
        </div>
      )}

      {/* в•ђв•ђв•ђ FILES в•ђв•ђв•ђ */}
      {activeTab==='files' && (
        <div className="card">
          <div className="card-header" style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div className="card-title">{uiText("Вложения")}</div>
            {canParticipate && !auditLocked && (
              <><button type="button" className="btn btn-outline btn-sm" onClick={()=>fileRef.current?.click()}>{uiText("Загрузить файл")}</button><input ref={fileRef} type="file" style={{display:'none'}} onChange={handleFileUpload}/></>
            )}
          </div>
          <div className="card-body">
            <EvidenceBrowser files={audit.files || []} questions={allQuestions} issues={audit.issues || []} canDelete={canDeleteAttachment} onDelete={handleFileDelete} deletingFileId={deletingFileId} onOpenContext={openBlocker} />
          </div>
        </div>
      )}

      {activeTab==='admin' && canManageTeam && (
        <div style={{display:'grid',gap:12}}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">{uiText("Команда аудита и права")}</div>
              <button type="button" className="btn btn-primary btn-sm" onClick={()=>setShowAssignModal(true)}>{uiText("Изменить состав")}</button>
            </div>
            <div className="card-body" style={{display:'grid',gap:10}}>
              <div style={{fontSize:13,color:'var(--premium-text-secondary)'}}>{uiText("Администратор может назначать и переназначать аудиторов из карточки аудита и из календаря. Аудитор получает приглашение, подтверждает участие или отказывается с причиной.")}</div>
              {teamAssignments.length === 0 ? (
                <div className="empty-state" style={{padding:24}}><p>{uiText("Пока никто не назначен")}</p></div>
              ) : teamAssignments.map(item => (
                <div key={item.id} style={{border:'1px solid #E5E7EB',borderRadius:12,padding:'12px 14px',display:'flex',justifyContent:'space-between',gap:12,alignItems:'flex-start',flexWrap:'wrap'}}>
                  <div>
                    <div style={{fontWeight:700}}>{item.auditor_name || uiText("Аудитор #{v0}", {v0: (item.auditor_user_id)})}</div>
                    <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:3}}>{item.team_role_label || item.team_role || uiText("Роль не задана")} · {item.auditor_job_title || uiText("Должность не заполнена")}</div>
                    {!!item.auditor_specializations?.length && <div style={{fontSize:12,color:'var(--premium-text-secondary)',marginTop:4}}>{uiText("Скиллы: ")}{item.auditor_specializations.join(', ')}</div>}
                    {item.note && <div style={{fontSize:12,color:'#444',marginTop:4}}>{uiText("Примечание: ")}{item.note}</div>}
                    {item.response_note && <div style={{fontSize:12,color:'#444',marginTop:4}}>{uiText("Ответ аудитора: ")}{item.response_note}</div>}
                  </div>
                  <span className={`badge ${item.status==='accepted'?'badge-green':item.status==='pending'?'badge-orange':'badge-red'}`}>
                    {item.status==='accepted'?uiText("Подтверждён"):item.status==='pending'?uiText("Ожидает ответа"):uiText("Отказался")}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* в•ђв•ђв•ђ HISTORY в•ђв•ђв•ђ */}
      {activeTab==='history' && (
        <div className="card">
          <div className="card-body">
            {(!audit.history||!audit.history.length) && <div style={{color:'var(--premium-text-secondary)',textAlign:'center',padding:16}}>{uiText("История пуста")}</div>}
            {(audit.history||[]).map(h=>(
              <div key={h.id} className="history-item">
                <div className="history-dot"/>
                <div style={{flex:1}}>
                  <span style={{fontWeight:600}}>{h.action}</span>
                  {h.user && <span style={{color:'var(--premium-text-secondary)'}}> — {h.user.full_name}</span>}
                  {h.details && <div style={{color:'var(--premium-text-secondary)',fontSize:12}}>{h.details}</div>}
                </div>
                <div style={{color:'var(--premium-text-secondary)',fontSize:11}}>{fmt(h.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showReportExport && !isCustomer && (
        <AuditReportExportModal auditId={id} auditTitle={audit.title} onClose={() => setShowReportExport(false)}
          blockedReason={answerBuffer.recovery || expectationBuffer.recovery
            ? uiText("Сначала восстановите черновик или оставьте данные сервера. Отчёт содержит только сохранённые данные.")
            : answerConflicts.length ? uiText("Сначала разрешите конфликт ответов. Затем можно скачать актуальный отчёт.")
            : answerBuffer.needsSync || expectationBuffer.needsSync || draftState.saving || saving || Object.values(uploadProgress).some(progress => progress > 0 && progress < 100)
              ? uiText("Дождитесь сохранения ответов и загрузки файлов. Если есть ошибка сохранения, закройте это окно и повторите сохранение.") : ''} />
      )}

      {questionToRemove && isAdmin && (
        <RemoveAuditQuestionDialog question={questionToRemove} onClose={() => setQuestionToRemove(null)} onRemove={handleQuestionRemoval} />
      )}

      {showAssignModal && canManageTeam && (
        <AssignAuditorsModal
          audit={audit}
          onClose={() => setShowAssignModal(false)}
          onAssigned={load}
        />
      )}

      {expectedAnswerQuestion && (
        <ExpectedAnswerModal
          question={expectedAnswerQuestion}
          defaultDocumentId={expectedAnswers[questionFrontendKey(expectedAnswerQuestion)]?.source_document_id || defaultExpectedDocumentId}
          generating={expectedAnswerGenerating}
          onGenerate={generateExpectedAnswer}
          onClose={() => !expectedAnswerGenerating && setExpectedAnswerQuestion(null)}
        />
      )}

      {/* Модалка приостановки аудита */}
      {showSuspendModal && (
        <Dialog size="sm" label={uiText("Приостановить аудит")} onClose={() => setShowSuspendModal(false)} closeDisabled={suspendBusy}>
            <div className="ap-dialog__header">
              <div className="ap-dialog__title" data-dialog-heading tabIndex={-1}>{uiText("Приостановить аудит")}</div>
              <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} disabled={suspendBusy} onClick={() => setShowSuspendModal(false)}>✕</button>
            </div>
            <div className="ap-dialog__body">
              <div className="ap-dialog__note">{uiText("По регламенту п. 4.4.4 аудит можно приостановить при отказе в доступе, форс-мажорах или длительной неготовности проверяемого. Все правки ответов и замечаний будут заблокированы до возобновления.")}</div>
              <div className="form-group">
                <label className="form-label required">{uiText("Причина приостановки")}</label>
                <textarea
                  className="ap-control"
                  rows={3}
                  value={suspendReason}
                  onChange={e => setSuspendReason(e.target.value)}
                  placeholder={uiText("Например: проверяемый отказал в доступе к складам")}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Плановая дата возобновления (опционально)")}</label>
                <input
                  type="date"
                  className="ap-control"
                  value={suspendUntil}
                  onChange={e => setSuspendUntil(e.target.value)}
                />
                <div style={{ fontSize: 12, color:'var(--premium-text-secondary)', marginTop: 4 }}>{uiText("Если оставить пусто — без срока, возобновление по решению админа.")}</div>
              </div>
            </div>
            <div className="ap-dialog__footer">
              <button type="button" className="ap-button ap-button--secondary" onClick={() => setShowSuspendModal(false)} disabled={suspendBusy}>{uiText("Отмена")}</button>
              <button type="button" className="ap-button ap-button--primary" onClick={handleSuspend} disabled={suspendBusy || !suspendReason.trim()}>
                {suspendBusy ? uiText("Приостанавливаем...") : uiText("Приостановить")}
              </button>
            </div>
        </Dialog>
      )}
    </div>
  );
}
